import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Listbox, Transition } from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon
} from "@heroicons/react/20/solid";
import "./style.scss";
import { Info } from "lucide-react";
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import Accordion from "react-bootstrap/Accordion";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { Question } from "types/schoolAdmin/questionTypes";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { store, useTypedSelector } from "redux/store";
import {
  createManualQuestionPaper,
  GridPreviewAsync,
  selectGridPreviewDataList,
} from "redux/features/manualQuestionPaperSlice";
import { resetList, clearGridPreview } from "redux/features/manualQuestionPaperSlice";
import { collapseIcon, editIcon } from "assets";
import { useDispatch } from "react-redux";
import {
  ChapterListAsync,
  DisplayChapterDataList,
} from "redux/features/chaptersSlice";
import {
  DisplayTopicDataList,
  TopicListAsync,
} from "redux/features/topicSlice";
import {
  CreateQuestionAsync,
  DisplayQuestionDataList,
  QuestionListAsync,
  updateQuestionAsync,
} from "redux/features/questionSlice";
import DOMPurify from "dompurify";
import {
  createInstructionAsync,
  DisplayInstructionDataList,
  InstructionListAsync,
  updateInstructionAsync,
} from "redux/features/instructionSlice";
import { Button, Table, Form } from "react-bootstrap";
import { toastError, toastInfo, toastWarning } from "helpers/toastHelper";
import {
  ClassesListAsync,
  DisplayClassesDataList,
} from "redux/features/classesSlice";
import {
  DisplaySubjectDataList,
  SubjectListAsync,
} from "redux/features/subjectSlice";
import {
  QuestionPaperPreviewAsync,
  selectQuestionPaperPreviewDataList,
} from "redux/features/QuestionPaperListSlice";
import { CKEditor } from "ckeditor4-react";
import { Loader } from "components";
import LiveViewQuestionPaperComponent from "../../live-preview-paper";
import CreateQuestions from "pages/school-admin/create-questions";

// Static data here //
const skill = [{ name: "Low" }, { name: "Medium" }, { name: "High" }];

const questionType = [
  { name: "Multiple-choice" },
  { name: "True / False" },
  { name: "Match Making" },
  { name: "Very Short Answer" },
  { name: "Short answer" },
  { name: "Long" },
  { name: "Paragraph" }
];

const level = [
  { name: "Easy" },
  { name: "Medium" },
  { name: "Difficult" },
  { name: "HOTS" },
];

const questionMarks = [
  { name: "1" },
  { name: "1.5" },
  { name: "2" },
  { name: "2.5" },
  { name: "3" },
  { name: "3.5" },
  { name: "4" },
  { name: "4.5" },
  { name: "5" },
  { name: "5.5" },
  { name: "6" },
  { name: "6.5" },
  { name: "7" },
  { name: "7.5" },
  { name: "8" },
  { name: "8.5" },
  { name: "9" },
  { name: "9.5" },
  { name: "10" },
  { name: "10.5" },
  { name: "11" },
  { name: "11.5" },
  { name: "12" }
];

// Define Question paper States here //
const initialValues: Question.ManualQuestionPaperType = {
  chapter_id: null,
  topic_id: null,
  skill: "",
  questionType: "",
  level: "",
  questionMarks: [],
  subject: "",
};

// validations message here //
const validationSchema = yup.object().shape({
  questionMarks: yup.array().of(yup.string()).min(1, "Please select at least one Mark"),
});

const QUESTIONS_PER_PAGE = 10;

const stripHtmlTags = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

// Returns true if ALL options are short (< 50 words) → use 2-col grid layout.
// Returns false if ANY option is long (>= 50 words) → use vertical list layout.
const optionsUseGrid = (options: any[]): boolean => {
  if (!options || options.length === 0) return false;
  return options.every((opt) => {
    const text = stripHtmlTags(opt?.data?.data || opt?.data || "");
    const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
    return wordCount < 50;
  });
};

// Compute effective marks for a question. If sub-questions exist, sum their marks; otherwise use question.max_marks
const getQuestionEffectiveMarks = (question: any): number => {
  try {
    if (Array.isArray(question?.sub_questions) && question.sub_questions.length > 0) {
      return question.sub_questions.reduce((sum: number, sq: any) => {
        const mark = Number(sq?.max_marks ?? 0);
        return sum + (isNaN(mark) ? 0 : mark);
      }, 0);
    }
    const mark = Number(question?.max_marks ?? 0);
    return isNaN(mark) ? 0 : mark;
  } catch {
    const mark = Number(question?.max_marks ?? 0);
    return isNaN(mark) ? 0 : mark;
  }
};

// CreateManual Question Papers Component here  //
const CreateRoutingBasedManualQuestionPapersComponent = () => {
  // Manage states here //
  const [accordionIndex, setAccordionIndex] = useState("0");
  const [accordionPanelValue, setAccordionPanelValue] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState<any>(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [filteredQuestionList, setFilteredQuestionList] = useState<any[]>([]);
  const [qIdSearchInput, setQIdSearchInput] = useState<string>("");
  const [searchedQuestions, setSearchedQuestions] = useState<any[] | null>(null);
  const [searchMessage, setSearchMessage] = useState<string>("");
  const [isApiSearchActive, setIsApiSearchActive] = useState<boolean>(false);
  const [allSearchedQuestions, setAllSearchedQuestions] = useState<any[]>([]); // Track all searched questions including deleted ones
  const [classData, setClassData] = useState<any>("");
  const [subjectData, setSubjectData] = useState<any>("");
  const [previewSubjectData, setPreviewSubjectData] = useState<any>("");
  const [remainingMarks, setRemainingMarks] = useState<any>("");
  const [remainingQuestions, setRemainingQuestions] = useState<any>("");
  const [deletedQuestions, setDeletedQuestions] = useState<any[]>([]);
  const [questionCount, setQuestionCount] = useState(0);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([]);
  const [selectedMarkValue, setSelectedMarkValue] = useState<number | null>(null);
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [loaderReferenceFromToolComponentInfoLoader, setLoaderReferenceFromToolComponentInfoLoader] = useState<boolean>(false);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const [pageCount, setPageCount] = useState(1);
  const [filterLoader, setFilterLoader] = useState<boolean>(false);

  // Pagination state for cumulative data loading
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [accumulatedQuestions, setAccumulatedQuestions] = useState<any[]>([]);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentFilterPayload, setCurrentFilterPayload] = useState<any>(null);


  const [previewShowModal, setPreviewShowModal] = React.useState(false);
  const [previewPayload, setPreviewPayload] = useState<any | null>(null);


  const handlePreviewModalClose = () => setPreviewShowModal(false);
  const handlePreviewModalShow = () => {
    const data = buildLiveViewPreviewData();
    setPreviewPayload(data);
    setPreviewShowModal(true);
  };

  const buildLiveViewPreviewData = () => {
    const dataJson = initialState?.data_json || EditQuestionData?.data_json || [];

    const builtSections = (dataJson.length
      ? dataJson.map((section, index) => {
        const sectionKey = `section_${section.section}_${index}`;
        const questions =
          sectionQuestions[sectionKey]?.filter((q) => !deletedQuestions.includes(q.id)) ||
          section.questions ||
          [];

        // Build a flat list of all questions including sub-questions for the preview
        // This ensures sub-questions can be found by ID in the preview component
        const allQuestionsForPreview: any[] = [];
        questions.forEach((q: any) => {
          // Add the parent question
          allQuestionsForPreview.push(q);
          // Add sub-questions if they exist
          if (Array.isArray(q?.sub_questions) && q.sub_questions.length > 0) {
            q.sub_questions.forEach((sq: any, idx: number) => {
              // Ensure sub-question has all required properties
              if (sq) {
                // Generate consistent ID - use existing ID or create one based on parent ID and index
                const subQuestionId = sq?.id || `sub_${q.id}_${idx}`;
                allQuestionsForPreview.push({
                  ...sq,
                  id: subQuestionId,
                  question_text: sq?.question_text || sq?.content || "-",
                  max_marks: sq?.max_marks || 0,
                });
              }
            });
          }
        });

        const items: any[] = [];
        for (let i = 0; i < (questions?.length || 0); i++) {
          const q = questions[i];

          if (Array.isArray(q?.sub_questions) && q.sub_questions.length > 0) {
            const buildCompositeNode = (baseQ: any) => {
              const subItems = baseQ.sub_questions.map((sq: any, idx: number) => {
                // Use a unique ID for each sub-question
                const subQuestionId = sq?.id || `sub_${baseQ.id}_${idx}`;
                return {
                  label: String.fromCharCode(65 + idx),
                  nodeType: "question",
                  questionId: subQuestionId,
                  marks: Number(sq?.max_marks || 0),
                };
              });
              const totalMarksForComposite = subItems.reduce(
                (sum: number, it: any) => sum + Number(it?.marks || 0),
                0
              );
              return {
                nodeType: "composite",
                displayLabel: `Q${items.length + 1}`,
                totalMarks: totalMarksForComposite,
                marks: totalMarksForComposite,
                questionId: baseQ.id,
                question_id: subItems.map((item: any) => item.questionId),
                items: subItems,
              } as const;
            };

            const mainComposite = buildCompositeNode(q);
            const groupItems: any[] = [mainComposite];
            let j = i + 1;
            while (j < (questions?.length || 0) && questions[j]?.displayType === "OR") {
              const alt = questions[j];
              if (Array.isArray(alt?.sub_questions) && alt.sub_questions.length > 0) {
                groupItems.push(buildCompositeNode(alt));
              } else {
                groupItems.push({ nodeType: "question", questionId: alt.id, marks: Number(alt?.max_marks || 0) });
              }
              j++;
            }
            if (groupItems.length > 1) {
              items.push({
                nodeType: "group",
                groupType: "OR",
                pickCount: 1,
                marks: Number(groupItems[0]?.marks || 0),
                displayLabel: "Answer any one",
                question_id: groupItems.map((item: any) => item.questionId || item.id),
                items: groupItems,
              });
              i = j - 1;
            } else {
              items.push(mainComposite);
            }
            continue;
          }

          if (q?.displayType === "OR") {
            const groupItems: any[] = [];
            let j = i;
            while (j < questions.length && questions[j]?.displayType === "OR") {
              groupItems.push({ nodeType: "question", questionId: questions[j].id, marks: Number(questions[j]?.max_marks || 0) });
              j++;
            }
            items.push({ nodeType: "group", groupType: "OR", pickCount: 1, marks: Number(groupItems[0]?.marks || 0), displayLabel: "Answer any one", question_id: groupItems.map((item: any) => item.questionId || item.id), items: groupItems });
            i = j - 1;
            continue;
          }
          const groupItems: any[] = [{ nodeType: "question", questionId: q.id, marks: Number(q?.max_marks || 0) }];
          let j = i + 1;
          while (j < questions.length && questions[j]?.displayType === "OR") {
            groupItems.push({ nodeType: "question", questionId: questions[j].id, marks: Number(questions[j]?.max_marks || 0) });
            j++;
          }
          if (groupItems.length > 1) {
            items.push({ nodeType: "group", groupType: "OR", pickCount: 1, marks: Number(groupItems[0]?.marks || 0), displayLabel: "Answer any one", question_id: groupItems.map((item: any) => item.questionId || item.id), items: groupItems });
            i = j - 1;
          } else {
            items.push(groupItems[0]);
          }
        }

        const sectionPickCount = items.reduce((acc, it) => acc + (it.nodeType === "group" ? 1 : 1), 0);
        const totalSectionMarks = items.reduce((sum: number, it: any) => sum + Number(it?.marks || 0), 0);
        const sectionSubject = sectionSubjects[sectionKey];

        // Preserve subject_id from original section data if it exists, otherwise use sectionSubjects state
        const finalSubjectId = section?.subject_id !== undefined && section?.subject_id !== null
          ? section.subject_id
          : sectionSubject;

        return {
          section: section?.section,
          max_marks: section?.max_marks ?? Number((items?.[0]?.marks) || 0),
          no_of_ques: section?.no_of_ques || sectionPickCount,
          total_section_marks: section?.total_section_marks ?? totalSectionMarks,
          attemptable_questions: section?.attemptable_questions || sectionPickCount,
          question_id: (questions || []).map((q: any) => q.id),
          items,
          instruction: section?.instruction,
          ...(finalSubjectId !== undefined && finalSubjectId !== null && { subject_id: finalSubjectId }),
          questions: allQuestionsForPreview, // Use the enhanced questions array that includes sub-questions
        };
      })
      : [
        (() => {
          const questions = sectionQuestions["default-section"]?.filter((q) => !deletedQuestions?.includes(q?.id)) || [];

          // Build a flat list of all questions including sub-questions for the preview
          const allQuestionsForPreview: any[] = [];
          questions.forEach((q: any) => {
            // Add the parent question
            allQuestionsForPreview.push(q);
            // Add sub-questions if they exist
            if (Array.isArray(q?.sub_questions) && q.sub_questions.length > 0) {
              q.sub_questions.forEach((sq: any, idx: number) => {
                // Ensure sub-question has all required properties
                if (sq) {
                  // Generate consistent ID - use existing ID or create one based on parent ID and index
                  const subQuestionId = sq?.id || `sub_${q.id}_${idx}`;
                  allQuestionsForPreview.push({
                    ...sq,
                    id: subQuestionId,
                    question_text: sq?.question_text || sq?.content || "-",
                    max_marks: sq?.max_marks || 0,
                  });
                }
              });
            }
          });

          const items: any[] = [];
          for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            if (Array.isArray(q?.sub_questions) && q.sub_questions.length > 0) {
              const buildCompositeNode = (baseQ: any) => {
                const subItems = baseQ.sub_questions.map((sq: any, idx: number) => {
                  // Use a unique ID for each sub-question
                  const subQuestionId = sq?.id || `sub_${baseQ.id}_${idx}`;
                  return {
                    label: String.fromCharCode(65 + idx),
                    nodeType: "question",
                    questionId: subQuestionId,
                    marks: Number(sq?.max_marks || 0),
                  };
                });
                const totalMarksForComposite = subItems.reduce((sum: number, it: any) => sum + Number(it?.marks || 0), 0);
                return { nodeType: "composite", displayLabel: `Q${items.length + 1}`, totalMarks: totalMarksForComposite, marks: totalMarksForComposite, questionId: baseQ.id, question_id: subItems.map((item: any) => item.questionId), items: subItems } as const;
              };
              const mainComposite = buildCompositeNode(q);
              const groupItems: any[] = [mainComposite];
              let j = i + 1;
              while (j < questions.length && questions[j]?.displayType === "OR") {
                const alt = questions[j];
                if (Array.isArray(alt?.sub_questions) && alt.sub_questions.length > 0) {
                  groupItems.push(buildCompositeNode(alt));
                } else {
                  groupItems.push({ nodeType: "question", questionId: alt.id, marks: Number(alt?.max_marks || 0) });
                }
                j++;
              }
              if (groupItems.length > 1) {
                items.push({ nodeType: "group", groupType: "OR", pickCount: 1, marks: Number(groupItems[0]?.marks || 0), displayLabel: "Answer any one", question_id: groupItems.map((item: any) => item.questionId || item.id), items: groupItems });
                i = j - 1;
              } else {
                items.push(mainComposite);
              }
              continue;
            }
            if (q?.displayType === "OR") {
              const groupItems: any[] = [];
              let j = i;
              while (j < questions.length && questions[j]?.displayType === "OR") {
                groupItems.push({ nodeType: "question", questionId: questions[j].id, marks: Number(questions[j]?.max_marks || 0) });
                j++;
              }
              items.push({ nodeType: "group", groupType: "OR", pickCount: 1, marks: Number(groupItems[0]?.marks || 0), displayLabel: "Answer any one", question_id: groupItems.map((item: any) => item.questionId || item.id), items: groupItems });
              i = j - 1;
              continue;
            }
            const groupItems: any[] = [{ nodeType: "question", questionId: q.id, marks: Number(q?.max_marks || 0) }];
            let j = i + 1;
            while (j < questions.length && questions[j]?.displayType === "OR") {
              groupItems.push({ nodeType: "question", questionId: questions[j].id, marks: Number(questions[j]?.max_marks || 0) });
              j++;
            }
            if (groupItems.length > 1) {
              items.push({ nodeType: "group", groupType: "OR", pickCount: 1, marks: Number(groupItems[0]?.marks || 0), displayLabel: "Answer any one", question_id: groupItems.map((item: any) => item.questionId || item.id), items: groupItems });
              i = j - 1;
            } else {
              items.push(groupItems[0]);
            }
          }
          const pickCount = items.reduce((acc, it) => acc + (it.nodeType === "group" ? 1 : 1), 0);
          const computedTotalMarks = items.reduce((sum: number, it: any) => sum + Number(it?.marks || 0), 0);
          const defaultSectionSubject = sectionSubjects["default-section"];
          return { section: "1", max_marks: Number((items?.[0]?.marks) || 0), no_of_ques: pickCount, total_section_marks: computedTotalMarks, attemptable_questions: pickCount, question_id: (questions || []).map((q: any) => q.id), instruction: "", items, ...(defaultSectionSubject && { subject_id: defaultSectionSubject }), questions: allQuestionsForPreview }; // Use the enhanced questions array that includes sub-questions
        })(),
      ]);

    const totalQuestionsAdded = builtSections?.reduce((total, section) => {
      const sectionPicks = (section.items || []).reduce((acc: number, it: any) => acc + (it.nodeType === "group" ? 1 : 1), 0);
      return total + sectionPicks;
    }, 0);

    const instruction = isEditing
      ? EditQuestionData?.instruction
      : (instructionList?.[0]
        ? { heading: instructionList[0]?.heading, content: instructionList[0]?.content }
        : (instructionList && instructionList.length === 0 ? null : undefined));
    // debugger;
    return {
      header_1: isEditing ? EditHeaderData?.header_1 : headerOne,
      header_2: isEditing ? EditHeaderData?.header_2 : headerTwo,
      total_questions: totalQuestionsAdded,
      type_test: isEditing ? EditQuestionData?.type_test : initialState?.type_test,
      question_paper_marks: isEditing ? EditQuestionData?.question_paper_marks : initialState?.total_marks,
      timing: isEditing ? EditQuestionData?.timing : initialState?.timing,
      grade: isEditing ? EditQuestionData?.grade : classData,
      subject: isEditing ? EditQuestionData?.subject : previewSubjectData,
      instruction,
      tag: EditQuestionData?.tag,
      data_json: builtSections,
    } as any;
  };

  const handleNewQuestionSuccess = async () => {
    // When a new question is added from the modal:
    // 1. Show loader while refreshing question list
    // 2. Bring user back to the first accordion section (FILTERS)

    // Open FILTERS accordion section
    setAccordionIndex("0");
    setAccordionPanelValue(true);

    // Refresh the question list with current filters
    let { questionMarks, topic_id, skill, questionType, level } = values;

    const payload = {
      ...(topic_id && { topicId: [Number(topic_id)] }),
      ...(skill && { skillId: [skill] }),
      ...(level && { levelId: [level] }),
      ...(questionMarks?.length && { marksId: questionMarks }),
      ...(questionType && { typeId: [questionType] }),
      ...(selectedChapter && { chapterId: [Number(selectedChapter.id)] }),
    };

    try {
      setFilterLoader(true);
      await dispatch(QuestionListAsync(payload));
    } finally {
      setFilterLoader(false);
    }

    // Close the modal
    handleNewQuestionClose();

    // Show success message
    toast.success("Question added successfully!");
  };

  // **Calculate total pages dynamically based on actual content height**
  const PAGE_HEIGHT = 1123;

  const calculatePages = useCallback(() => {
    const contentElement = contentRef.current;

    if (!contentElement) return;

    setTimeout(() => {
      const contentHeight = contentElement.scrollHeight;
      const estimatedPages = Math.ceil(contentHeight / PAGE_HEIGHT);

      setPageCount(estimatedPages || 1);
    }, 500);
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;

    const observer = new ResizeObserver(() => {
      calculatePages();
    });

    observer.observe(contentRef.current);

    return () => observer.disconnect();
  }, [calculatePages]);

  // **Recalculate when images are loaded**
  useEffect(() => {
    if (!contentRef.current) return;

    const images = document.querySelectorAll("img");
    images.forEach((img) => img.addEventListener("load", calculatePages));

    return () => images.forEach((img) => img.removeEventListener("load", calculatePages));
  }, [calculatePages]);

  const [showNewQuestionPaper, setShowNewQuestionPaper] = useState(false);
  const handleNewQuestionClose = () => {
    setShowNewQuestionPaper(false);
    setShowRevisedQuestion(false);
  };

  const handleNewQuestionShow = () => setShowNewQuestionPaper(true);

  const [show, setShow] = useState(false);
  const dispatch = useDispatch<typeof store.dispatch>();

  const handleShow = () => setShow(true);
  const handleClose = () => setShow(false);
  const [qpCode, setQpCode] = useState("");
  const [headerOne, setHeaderOne] = useState("");
  const [headerTwo, setHeaderTwo] = useState("");

  // List data here //
  const { chaptersDataList }: any = useTypedSelector(DisplayChapterDataList);
  const EditQuestionData = useTypedSelector(selectQuestionPaperPreviewDataList);
  const TopicDataList = useTypedSelector(DisplayTopicDataList);
  const questionList = useTypedSelector(DisplayQuestionDataList);
  const [showRevisedQuestion, setShowRevisedQuestion] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [showRevisedInstruction, setShowRevisedInstruction] = useState(false);
  const [showAddInstruction, setShowAddInstruction] = useState(false);
  const [selectedInstructionId, setSelectedInstructionId] = useState(null);
  const [selectedInstructionText, setSelectedInstructionText] = useState("");
  const [selectedInstructionContent, setSelectedInstructionContent] =
    useState("");
  const [sectionQuestions, setSectionQuestions] = useState<Record<string, any[]>>({});
  const [sectionSubjects, setSectionSubjects] = useState<Record<string, any>>({});
  const [groupedSections, setGroupedSections] = useState<Record<string, any[]>>({});
  const [totalQuestionsCount, setTotalQuestionsCount] = useState(0);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [showFinalSubmitConfirm, setShowFinalSubmitConfirm] = useState(false);
  const toggleCollapse = (section) => {
    setCollapsedSections((prevState) => ({
      ...prevState,
      [section]: !prevState[section],
    }));
  };
  // add question show value pre-fill //
  const [editorQuestionsHtml, setEditorQuestionsHtml] = useState("");

  // Show Revised Question logic here //

  const handleShowRevisedQuestion = (id) => {
    const selectedQuestion = currentPageQuestions?.find(
      (item) => item?.id === id
    );

    if (selectedQuestion) {
      setEditorQuestionsHtml(selectedQuestion?.question_text);
      // Convert single max_marks to array format for consistency
      const marksValue = selectedQuestion?.max_marks
        ? [String(selectedQuestion.max_marks)]
        : [];
      setValidState({
        ...validState,
        questionType: selectedQuestion?.type,
        questionMarks: marksValue,
        level: selectedQuestion?.level,
        skill: selectedQuestion?.skill,
      });
      formik.setFieldValue("questionMarks", marksValue);

      // Pre-fill options for Multiple-choice or True / False type //
      const questionOptions = selectedQuestion?.question_options || [];
      const prefilledOptions = questionOptions.map((option) => ({
        data: option?.data || option?.data?.data,
      }));
      setOptionEditors(prefilledOptions);


      if (
        selectedQuestion?.type === "Multiple-choice" ||
        selectedQuestion?.type === "True / False"
      ) {
        if (selectedQuestion?.answer_text) {
          setSelectedAnswers(
            selectedQuestion?.answer_text.split(",").map(Number)
          );
        } else {
          setSelectedAnswers([]);
        }
      } else {
        setEditorAnswerHtml(selectedQuestion?.answer_text || "");
      }

      setSelectedQuestionId(id);
      setShowRevisedQuestion(true);
    }
  };

  // Show Revised Instruction logic here //
  const handleShowRevisedInstruction = (instruction) => {
    setSelectedInstructionId(instruction.id);
    setSelectedInstructionText(instruction?.heading);
    setSelectedInstructionContent(instruction?.content);
    setShowRevisedInstruction(true);
  };

  const handleCloseInstruction = () => {
    setSelectedInstructionId(null);
    setSelectedInstructionText("");
    setSelectedInstructionContent("");
    setShowRevisedInstruction(false);
  };

  const handleOpenAddInstruction = () => {
    setSelectedInstructionText("");
    setSelectedInstructionContent("");
    setShowAddInstruction(true);
  };

  const handleCloseAddInstruction = () => {
    setSelectedInstructionText("");
    setSelectedInstructionContent("");
    setShowAddInstruction(false);
  };

  const handleCloseQuestion = () => {
    setSelectedQuestionId(null);
    setEditorQuestionsHtml("");
    setEditorAnswerHtml("");
    setOptionEditors([]);
    setSelectedAnswers([]);
    setShowRevisedQuestion(false);
  };

  // Instruction update logic here //
  const handleUpdateInstruction = async (e) => {
    e.preventDefault();

    if (selectedInstructionId !== null) {
      const updatedDetails = {
        heading: selectedInstructionText,
        content: selectedInstructionContent,
      };

      await dispatch(
        updateInstructionAsync({
          id: selectedInstructionId,
          instructionDetails: updatedDetails,
        })
      );

      dispatch(
        InstructionListAsync({
          gradeId: initialState?.grade_id,
          subjectId: initialState?.subject_id,
        })
      );
      handleCloseInstruction();
    }
  };


  const handleAddInstruction = async (e) => {
    e.preventDefault();

    if (!selectedInstructionText.trim() || !selectedInstructionContent.trim()) {
      toast.error("Please fill in both heading and content");
      return;
    }

    const updatedDetails = {
      heading: selectedInstructionText,
      content: selectedInstructionContent,
      grade_id: initialState?.grade_id,
      subject_id: initialState?.subject_id,
    };

    await dispatch(createInstructionAsync(updatedDetails));

    await dispatch(
      InstructionListAsync({
        gradeId: initialState?.grade_id,
        subjectId: initialState?.subject_id,
      })
    );

    handleCloseAddInstruction();
  };


  // Delete Question logic in Question paper preview here //
  const handleDeleteQuestion = (section, questionId) => {
    setSectionQuestions((prev) => ({
      ...prev,
      [section]: prev[section].filter((q) => q?.id !== questionId),
    }));
    setDeletedQuestions((prev) => [...prev, questionId]);
  };

  // Edit Question in Drag and drop Question Accordian //
  const handleUpdateQuestion = async (e) => {
    e.preventDefault();
    if (selectedQuestionId !== null) {
      const options = optionEditors?.map((option, index) => ({
        data: option?.data || option?.data?.data,
        option: index + 1,
      }));

      const gradeId = isEditing
        ? EditQuestionData?.grade_id
        : initialState?.grade_id;
      const subjectId = isEditing
        ? EditQuestionData?.subject_id
        : initialState?.subject_id;

      const updatedDetails = {
        question_text: editorQuestionsHtml,
        type: validState?.questionType,
        grade_id: gradeId != null ? [Number(gradeId)] : [],
        subject_id: subjectId != null ? [Number(subjectId)] : [],
        max_marks: Array.isArray(validState?.questionMarks) && validState.questionMarks.length > 0
          ? Number(validState.questionMarks[0])
          : (validState?.questionMarks ? Number(validState.questionMarks) : null),
        level: validState?.level,
        skill: validState?.skill,
        answer_text:
          validState?.questionType === "Multiple-choice" ||
            validState?.questionType === "True / False"
            ? selectedAnswers.join(",")
            : editorAnswerHtml,
        question_options: options,
      };

      try {
        await dispatch(
          updateQuestionAsync({
            id: selectedQuestionId,
            questionDetails: updatedDetails,
          })
        );

        let { questionMarks, topic_id, skill, questionType, level } = values;

        const payload = {
          ...(topic_id && { topicId: [Number(topic_id)] }),
          ...(skill && { skillId: [skill] }),
          ...(level && { levelId: [level] }),
          ...(questionMarks?.length && { marksId: questionMarks }),
          ...(questionType && { typeId: [questionType] }),
          ...(selectedChapter && { chapterId: [Number(selectedChapter?.id)] }),
        };

        await dispatch(QuestionListAsync(payload));
        handleCloseQuestion();
      } catch (error) {
        toast.error("Failed to update question");
      }
    }
  };





  useEffect(() => {
    if (selectedQuestionId !== null) {
      const selectedQuestion = questionList.find(
        (question) => question.id === selectedQuestionId
      );
      if (selectedQuestion) {
        setEditorQuestionsHtml(selectedQuestion.question_text);
      }
    }
  }, [selectedQuestionId, questionList]);

  const [validState, setValidState] =
    useState<Question.ManualQuestionPaperType>(initialValues);
  const gridPreviewData = useTypedSelector(selectGridPreviewDataList);

  const renderCell = (item, header) => {
    if (header !== "Chapter Name") {
      return (
        <div
          className="form-control"
          style={{ border: "none", padding: "0.5rem 0" }}
        >
          {item[header] !== undefined ? item[header] : 0}
        </div>
      );
    }
    return item[header];
  };

  // Collect question ids already placed in the paper (exclude deleted)
  const existingQuestionIds = useMemo(() => {
    const ids = new Set<number>();
    Object.values(sectionQuestions || {}).forEach((arr: any[]) => {
      (arr || []).forEach((q: any) => {
        if (q?.id && !deletedQuestions.includes(q.id)) {
          ids.add(Number(q.id));
        }
        if (Array.isArray(q?.sub_questions)) {
          q.sub_questions.forEach((sq: any) => {
            if (sq?.id && !deletedQuestions.includes(sq.id)) {
              ids.add(Number(sq.id));
            }
          });
        }
      });
    });
    return ids;
  }, [sectionQuestions, deletedQuestions]);

  useEffect(() => {
    // If we have active API search results, use allSearchedQuestions as the base
    // Otherwise use accumulated questions or questionList
    const sourceQuestions = isApiSearchActive && allSearchedQuestions.length > 0
      ? allSearchedQuestions
      : accumulatedQuestions.length > 0
        ? accumulatedQuestions
        : (questionList || []).map((q: any) => ({
          ...q,
          displayType: q?.displayType || "Main",
        }));

    // Create a map to preserve existing displayType values from current filteredQuestionList
    const displayTypeMap = new Map<number, string>();
    filteredQuestionList.forEach((q: any) => {
      if (q?.id) {
        displayTypeMap.set(Number(q.id), q.displayType || "Main");
      }
    });

    // Filter out questions that are already in the paper (and not deleted)
    // AND preserve displayType from current state if it exists
    const filtered = sourceQuestions
      .filter((q: any) => !existingQuestionIds.has(Number(q?.id)))
      .map((q: any) => ({
        ...q,
        // Preserve existing displayType if available, otherwise use source value or default
        displayType: displayTypeMap.has(Number(q?.id))
          ? displayTypeMap.get(Number(q?.id))
          : (q?.displayType || "Main"),
      }));

    setFilteredQuestionList(filtered);

    // Also update searchedQuestions if API search is active
    if (isApiSearchActive && allSearchedQuestions.length > 0) {
      const searchedFiltered = filtered.filter((q: any) =>
        allSearchedQuestions.some((sq: any) => Number(sq?.id) === Number(q?.id))
      );
      setSearchedQuestions(searchedFiltered);
    }
  }, [accumulatedQuestions, questionList, existingQuestionIds, isApiSearchActive, allSearchedQuestions]);

  // Keep selected checkboxes in sync with the currently visible question list
  useEffect(() => {
    setSelectedQuestionIds((prev) => {
      const allowedIds = new Set(filteredQuestionList.map((q: any) => Number(q?.id)));
      const next = prev.filter((id) => allowedIds.has(id));
      if (next.length === 0) {
        setSelectedMarkValue(null);
      }
      return next;
    });
  }, [filteredQuestionList]);

  const navigate = useNavigate();

  const handleFormDraftSubmit: React.MouseEventHandler<HTMLButtonElement> = (
    event
  ) => {
    event.preventDefault();

    void handleFormSubmit(true);
  };




  // initial values here //
  const [initialState, setInitialState] = useState({
    total_marks: "",
    timing: "",
    total_questions: "",
    id: "",
    grade_id: [] as number[],
    subject_id: [] as number[],
    type_test: "",
    data_json: [],
  });

  const isEditing = !!EditQuestionData;

  // Edit Data get in manual create page in Edit mode //
  useEffect(() => {
    const normalizeItemsToQuestions = (
      items: any[] = [],
      questionLookup: Record<number, any> = {}
    ) => {
      const result: any[] = [];

      const toComposite = (item: any, displayType: string = "Main") => {
        const baseQuestion =
          questionLookup[item?.questionId || item?.id] || {};
        const subQuestions =
          item?.items?.map((sq: any) => {
            const subQuestionId = sq?.questionId || sq?.id;
            const subQuestionFromLookup = questionLookup[subQuestionId] || {};
            // Preserve all properties from the lookup, with fallbacks
            return {
              ...subQuestionFromLookup,
              id: subQuestionId,
              max_marks: sq?.marks || subQuestionFromLookup?.max_marks || 0,
              question_text:
                subQuestionFromLookup?.question_text ||
                sq?.question_text ||
                subQuestionFromLookup?.content ||
                "-",
            };
          }) || [];
        return {
          id: item?.questionId || item?.id,
          max_marks: item?.totalMarks ?? item?.marks,
          sub_questions: subQuestions,
          displayType,
          question_text:
            baseQuestion?.question_text ||
            item?.question_text ||
            "",
          question_options: baseQuestion?.question_options || [],
        };
      };

      items.forEach((item: any) => {
        if (item?.nodeType === "group" && item?.groupType === "OR") {
          const groupItems = item?.items || [];
          groupItems.forEach((gItem: any, idx: number) => {
            if (gItem?.nodeType === "composite") {
              result.push(toComposite(gItem, idx === 0 ? "Main" : "OR"));
            } else {
              const baseQuestion =
                questionLookup[gItem?.questionId || gItem?.id] || {};
              result.push({
                id: gItem?.questionId || gItem?.id,
                max_marks: gItem?.marks,
                displayType: idx === 0 ? "Main" : "OR",
                question_text:
                  baseQuestion?.question_text ||
                  gItem?.question_text ||
                  "",
                question_options: baseQuestion?.question_options || [],
              });
            }
          });
          return;
        }

        if (item?.nodeType === "composite") {
          result.push(toComposite(item, "Main"));
          return;
        }

        if (item?.nodeType === "question") {
          const baseQuestion =
            questionLookup[item?.questionId || item?.id] || {};
          result.push({
            id: item?.questionId || item?.id,
            max_marks: item?.marks,
            displayType: "Main",
            question_text:
              baseQuestion?.question_text ||
              item?.question_text ||
              "",
            question_options: baseQuestion?.question_options || [],
          });
        }
      });

      return result;
    };
    const normalizeQuestionsArray = (questions: any[] = []) =>
      (questions || []).map((q: any) => ({
        ...q,
        displayType: q?.displayType || "Main",
        // Preserve sub_questions if they exist
        sub_questions: Array.isArray(q?.sub_questions)
          ? q.sub_questions.map((sq: any) => ({
            ...sq,
            id: sq?.id || `sub_${q.id}_${sq?.id || Math.random()}`,
            question_text: sq?.question_text || sq?.content || "-",
            max_marks: sq?.max_marks || 0,
          }))
          : q?.sub_questions,
      }));

    const data = isEditing
      ? EditQuestionData?.data_json
      : initialState?.data_json;

    // Group sections by section letter + subject_id for display
    const groupedData: Record<string, any[]> = {};
    const sectionData: Record<string, any[]> = {};
    const subjectsData: Record<string, any> = {};

    data?.forEach((section, index) => {
      const subjectId = section?.subject_id || 'no_subject';
      const groupKey = `section_${section?.section}_${subjectId}`;

      // Store individual section data with index for payload generation
      const sectionKey = `section_${section?.section}_${index}`;
      const questionLookup: Record<number, any> = {};
      (section?.questions || []).forEach((q: any) => {
        if (q?.id) {
          questionLookup[Number(q.id)] = q;
        }
        // Also include sub-questions in the lookup
        if (Array.isArray(q?.sub_questions)) {
          q.sub_questions.forEach((sq: any) => {
            if (sq?.id) {
              questionLookup[Number(sq.id)] = sq;
            }
          });
        }
      });

      const normalizedQuestions =
        (section?.items && section?.items?.length
          ? normalizeItemsToQuestions(section?.items, questionLookup)
          : normalizeQuestionsArray(section?.questions)) || [];
      sectionData[sectionKey] = normalizedQuestions;

      // Group sections by letter + subject for display
      if (!groupedData[groupKey]) {
        groupedData[groupKey] = [];
      }
      groupedData[groupKey].push({
        ...section,
        originalIndex: index,
        sectionKey: sectionKey,
        questions: normalizedQuestions,
      });

      // Store subject for this grouped section
      if (section?.subject_id) {
        subjectsData[groupKey] = section.subject_id;
      }
    });

    setSectionQuestions(sectionData);
    setSectionSubjects(subjectsData);
    setGroupedSections(groupedData);
  }, [isEditing, EditQuestionData, initialState]);

  useEffect(() => {
    const sourceData = EditQuestionData;
    if (!sourceData) return;

    setInitialState({
      total_marks: sourceData?.question_paper_marks,
      timing: sourceData?.timing,
      total_questions: sourceData?.total_questions,
      id: sourceData?.id,
      grade_id: [Number(sourceData?.grade_id)],
      subject_id: [Number(sourceData?.subject_id)],
      type_test: sourceData?.type_test,
      data_json: sourceData?.data_json || [],
    });

    setValidState((prevState) => ({
      ...prevState,
      question_paper_marks: sourceData?.question_paper_marks,
      timing: sourceData?.timing,
      total_questions: sourceData?.total_questions,
      id: sourceData?.id,
      grade_id: [Number(sourceData?.grade_id)],
      subject_id: [Number(sourceData?.subject_id)],
      type_test: sourceData?.type_test,
      data_json: sourceData?.data_json || [],
    }));
  }, [EditQuestionData]);

  // filtered chapter based on class and subject //

  useEffect(() => {
    const subjectId =
      initialState?.subject_id?.[0] || EditQuestionData?.subject_id;
    const gradeId =
      initialState?.grade_id?.[0] || EditQuestionData?.grade_id;

    if (subjectId && gradeId) {
      dispatch(
        ChapterListAsync({
          statusId: true,
          subjectId: [subjectId],
          gradeId: [gradeId],
        })
      );
    }
  }, [dispatch, EditQuestionData, initialState?.grade_id, initialState?.subject_id]);


  const renderContent = useCallback((content = "") => {
    const sanitizedBlog = DOMPurify.sanitize(content);
    // return <span dangerouslySetInnerHTML={{ __html: sanitizedBlog }}></span>;
    return <div className="question-html" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sanitizedBlog) }} />
  }, []);

  const handleMultiStepForm = (index: any) => {
    setAccordionIndex(index);
  };

  // Accordian logic here //
  const handleAccordionPanel = (index) => {
    if (!isValid && index == "1") {
      toast.warning("Please fill the filter form first!");
    } else {
      const isShowPanel = !accordionPanelValue;
      setAccordionPanelValue(isShowPanel);
      isShowPanel ? setAccordionIndex(index) : setAccordionIndex("5");
    }
  };

  // Question paper submit logic here //

  const onSubmit = async (values) => {
    setFilterLoader(true);
    let { questionMarks, topic_id, skill, questionType, level } = values;

    const payload = {
      ...(topic_id && { topicId: [Number(topic_id)] }),
      ...(skill && { skillId: [skill] }),
      ...(level && { levelId: [level] }),
      ...(questionMarks?.length && { marksId: questionMarks }),
      ...(questionType && { typeId: [questionType] }),
      ...(selectedChapter && { chapterId: [Number(selectedChapter.id)] }),
      ...(subjectData?.id && { subjectId: [Number(subjectData.id)] }),
      ...(classData?.id && { gradeId: [Number(classData.id)] }), // Use actual selected grade_id
    };

    if (!questionMarks || !Array.isArray(questionMarks) || questionMarks.length === 0) {
      setIsValid(false);
      setFilterLoader(false)
    } else {
      setIsValid(true);
      try {
        // Reset pagination state
        setCurrentPage(1);
        setAccumulatedQuestions([]);
        setCurrentFilterPayload(payload);

        // Fetch first page with pagination
        const response = await dispatch(QuestionListAsync({
          ...payload,
          page: 1,
          pageSize: pageSize,
        }));

        if (response?.meta?.requestStatus === "fulfilled") {
          const { questions, count } = response.payload;

          // Set states with response data
          setQuestionCount(count);
          setTotalCount(count);

          // Accumulate first page data
          const questionsWithDisplayType = (questions || []).map((q: any) => ({
            ...q,
            displayType: q?.displayType || "Main",
          }));
          setAccumulatedQuestions(questionsWithDisplayType);

          // Check if there are more pages
          const totalPages = Math.ceil(count / pageSize);
          setHasMorePages(totalPages > 1);

          setFilterLoader(false);
        } else {
          console.error("Failed to fetch questions: Unknown error");
          setFilterLoader(false);
        }
      } catch (error) {
        console.error("An error occurred while fetching questions:", error);
        setFilterLoader(false);
      }
      handleMultiStepForm("2");
    }
  };

  // Function to load next page and accumulate data
  const loadNextPage = async () => {
    if (!hasMorePages || isLoadingMore || !currentFilterPayload) {
      return;
    }

    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const response = await dispatch(QuestionListAsync({
        ...currentFilterPayload,
        page: nextPage,
        pageSize: pageSize,
      }));

      if (response?.meta?.requestStatus === "fulfilled") {
        const { questions, count } = response.payload;

        // Add displayType to new questions
        const questionsWithDisplayType = (questions || []).map((q: any) => ({
          ...q,
          displayType: q?.displayType || "Main",
        }));

        // Accumulate with existing questions
        setAccumulatedQuestions((prev) => [...prev, ...questionsWithDisplayType]);
        setCurrentPage(nextPage);

        // Check if there are more pages
        const totalPages = Math.ceil(count / pageSize);
        setHasMorePages(nextPage < totalPages);
      }
    } catch (error) {
      console.error("An error occurred while loading more questions:", error);
      toast.error("Failed to load more questions");
    } finally {
      setIsLoadingMore(false);
    }
  };

  /**
   * Handle case when specific question IDs are provided in filter
   * This function can be called when you have a payload with id array in filter
   * 
   * Example usage:
   * handleQuestionIdFilter({
   *   filter: {
   *     id: [7606, 7607, 7609],
   *     grade_id: [11],
   *     subject_id: [34],
   *     max_marks: ["2", "1"]
   *   },
   *   range: {
   *     page: 1,
   *     pageSize: 10
   *   }
   * })
   * 
   * This will fetch questions matching the provided IDs and filters from the API
   */
  const handleQuestionIdFilter = async (filterPayload: any) => {
    setFilterLoader(true);
    setCurrentPage(1);
    setAccumulatedQuestions([]);

    try {
      // Extract IDs from filter if present
      const questionIds = filterPayload?.filter?.id;

      // Build payload for QuestionListAsync
      const payload: any = {
        ...(filterPayload?.filter?.grade_id && { gradeId: filterPayload.filter.grade_id }),
        ...(filterPayload?.filter?.subject_id && { subjectId: filterPayload.filter.subject_id }),
        ...(filterPayload?.filter?.max_marks && { marksId: filterPayload.filter.max_marks }),
        ...(questionIds && Array.isArray(questionIds) && questionIds.length > 0 && { id: questionIds }),
      };

      const response = await dispatch(QuestionListAsync({
        ...payload,
        page: filterPayload?.range?.page || 1,
        pageSize: filterPayload?.range?.pageSize || 100, // Large page size to get all matching questions
      }));

      if (response?.meta?.requestStatus === "fulfilled") {
        const { questions, count } = response.payload;

        const questionsWithDisplayType = (questions || []).map((q: any) => ({
          ...q,
          displayType: q?.displayType || "Main",
        }));

        setAccumulatedQuestions(questionsWithDisplayType);
        setQuestionCount(questionsWithDisplayType.length);
        setTotalCount(count || questionsWithDisplayType.length);
        setHasMorePages(false); // No pagination when filtering by specific IDs
        setCurrentFilterPayload(payload);
      }
    } catch (error) {
      console.error("An error occurred while fetching questions by ID:", error);
      toast.error("Failed to fetch questions");
    } finally {
      setFilterLoader(false);
    }
  };

  useEffect(() => { }, [questionCount]);

  // Logging the state after it updates

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  // Form submit logic here //
  const handleFormSubmit = async (
    isDraft: boolean = false,
    options?: { silent?: boolean }
  ) => {
    const silent = options?.silent || false;
    if (!isDraft) {
      const isComplete = Object.values(validState).every((value) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        } else {
          return value !== "";
        }
      });

      if (isComplete) {
        toast.warning("Complete the question paper");
        return;
      }
    }

    const dataJson =
      initialState?.data_json || EditQuestionData?.data_json || [];


    const data_json = dataJson.length
      ? dataJson.map((section, index) => {
        const sectionKey = `section_${section.section}_${index}`;
        const questions =
          sectionQuestions[sectionKey]?.filter(
            (q) => !deletedQuestions.includes(q.id)
          ) ||
          section.questions ||
          [];

        // Build grouped items: group a Main followed by one or more ORs
        const items: any[] = [];
        for (let i = 0; i < (questions?.length || 0); i++) {
          const q = questions[i];

          // If question is composite (has sub_questions), build composite node
          // and check for following OR alternatives (which may be simple or composite)
          if (Array.isArray(q?.sub_questions) && q.sub_questions.length > 0) {
            const buildCompositeNode = (baseQ: any) => {
              const subItems = baseQ.sub_questions.map((sq: any, idx: number) => ({
                label: String.fromCharCode(65 + idx),
                nodeType: "question",
                questionId: sq?.id ?? baseQ.id,
                marks: Number(sq?.max_marks || 0),
              }));
              const totalMarksForComposite = subItems.reduce(
                (sum: number, it: any) => sum + Number(it?.marks || 0),
                0
              );
              return {
                nodeType: "composite",
                displayLabel: `Q${items.length + 1}`,
                totalMarks: totalMarksForComposite,
                marks: totalMarksForComposite,
                questionId: baseQ.id,
                question_id: subItems.map((item: any) => item.questionId),
                items: subItems,
              } as const;
            };

            const mainComposite = buildCompositeNode(q);

            // collect consecutive OR items that follow
            const groupItems: any[] = [mainComposite];
            let j = i + 1;
            while (j < (questions?.length || 0) && questions[j]?.displayType === "OR") {
              const alt = questions[j];
              if (Array.isArray(alt?.sub_questions) && alt.sub_questions.length > 0) {
                groupItems.push(buildCompositeNode(alt));
              } else {
                groupItems.push({
                  nodeType: "question",
                  questionId: alt.id,
                  marks: Number(alt?.max_marks || 0),
                });
              }
              j++;
            }

            if (groupItems.length > 1) {
              items.push({
                nodeType: "group",
                groupType: "OR",
                pickCount: 1,
                marks: Number(groupItems[0]?.marks || 0),
                displayLabel: "Answer any one",
                question_id: groupItems.map((item: any) => item.questionId || item.id),
                items: groupItems,
              });
              i = j - 1;
            } else {
              items.push(mainComposite);
            }
            continue;
          }

          if (q?.displayType === "OR") {
            // If an OR appears first without a Main, treat it as its own group start
            const groupItems: any[] = [];
            // collect consecutive ORs
            let j = i;
            while (j < questions.length && questions[j]?.displayType === "OR") {
              groupItems.push({
                nodeType: "question",
                questionId: questions[j].id,
                marks: Number(questions[j]?.max_marks || 0),
              });
              j++;
            }
            items.push({
              nodeType: "group",
              groupType: "OR",
              pickCount: 1,
              marks: Number(groupItems[0]?.marks || 0),
              displayLabel: "Answer any one",
              question_id: groupItems.map((item: any) => item.questionId || item.id),
              items: groupItems,
            });
            i = j - 1;
            continue;
          }
          // Main item: see if followed by OR(s)
          const groupItems: any[] = [
            {
              nodeType: "question",
              questionId: q.id,
              marks: Number(q?.max_marks || 0),
            },
          ];
          let j = i + 1;
          while (j < questions.length && questions[j]?.displayType === "OR") {
            groupItems.push({
              nodeType: "question",
              questionId: questions[j].id,
              marks: Number(questions[j]?.max_marks || 0),
            });
            j++;
          }
          if (groupItems.length > 1) {
            items.push({
              nodeType: "group",
              groupType: "OR",
              pickCount: 1,
              marks: Number(groupItems[0]?.marks || 0),
              displayLabel: "Answer any one",
              question_id: groupItems.map((item: any) => item.questionId || item.id),
              items: groupItems,
            });
            i = j - 1;
          } else {
            items.push(groupItems[0]);
          }
        }

        // Count pickable questions (groups count as 1, single questions count as 1)
        const sectionPickCount = items.reduce((acc, it) => acc + (it.nodeType === "group" ? 1 : 1), 0);
        // Flat question ids for backward compatibility
        const flatQuestionIds = (questions || []).map((q: any) => q.id);
        // Total section marks based on pickable items (group counted once)
        const totalSectionMarks = items.reduce((sum: number, it: any) => sum + Number(it?.marks || 0), 0);

        const sectionSubject = sectionSubjects[sectionKey];

        // Preserve subject_id from original section data if it exists, otherwise use sectionSubjects state
        const finalSubjectId = section?.subject_id !== undefined && section?.subject_id !== null
          ? section.subject_id
          : sectionSubject;

        return {
          section: section?.section,
          max_marks: section?.max_marks ?? Number((items?.[0]?.marks) || 0),
          no_of_ques: section?.no_of_ques || sectionPickCount,
          total_section_marks: section?.total_section_marks ?? totalSectionMarks,
          attemptable_questions: section?.attemptable_questions || sectionPickCount,
          question_id: flatQuestionIds,
          items,
          instruction: section?.instruction,
          ...(finalSubjectId !== undefined && finalSubjectId !== null && { subject_id: finalSubjectId }),
        };
      })
      : [
        (() => {
          const questions = sectionQuestions["default-section"]?.filter((q) => !deletedQuestions?.includes(q?.id)) || [];
          const items: any[] = [];
          for (let i = 0; i < questions.length; i++) {
            const q = questions[i];

            // Composite question handling in default section, with OR grouping support
            if (Array.isArray(q?.sub_questions) && q.sub_questions.length > 0) {
              const buildCompositeNode = (baseQ: any) => {
                const subItems = baseQ.sub_questions.map((sq: any, idx: number) => ({
                  label: String.fromCharCode(65 + idx),
                  nodeType: "question",
                  questionId: sq?.id ?? baseQ.id,
                  marks: Number(sq?.max_marks || 0),
                }));
                const totalMarksForComposite = subItems.reduce(
                  (sum: number, it: any) => sum + Number(it?.marks || 0),
                  0
                );
                return {
                  nodeType: "composite",
                  displayLabel: `Q${items.length + 1}`,
                  totalMarks: totalMarksForComposite,
                  marks: totalMarksForComposite,
                  questionId: baseQ.id,
                  question_id: subItems.map((item: any) => item.questionId),
                  items: subItems,
                } as const;
              };

              const mainComposite = buildCompositeNode(q);

              // collect consecutive OR items that follow
              const groupItems: any[] = [mainComposite];
              let j = i + 1;
              while (j < questions.length && questions[j]?.displayType === "OR") {
                const alt = questions[j];
                if (Array.isArray(alt?.sub_questions) && alt.sub_questions.length > 0) {
                  groupItems.push(buildCompositeNode(alt));
                } else {
                  groupItems.push({ nodeType: "question", questionId: alt.id, marks: Number(alt?.max_marks || 0) });
                }
                j++;
              }

              if (groupItems.length > 1) {
                items.push({ nodeType: "group", groupType: "OR", pickCount: 1, marks: Number(groupItems[0]?.marks || 0), displayLabel: "Answer any one", question_id: groupItems.map((item: any) => item.questionId || item.id), items: groupItems });
                i = j - 1;
              } else {
                items.push(mainComposite);
              }
              continue;
            }

            if (q?.displayType === "OR") {
              const groupItems: any[] = [];
              let j = i;
              while (j < questions.length && questions[j]?.displayType === "OR") {
                groupItems.push({ nodeType: "question", questionId: questions[j].id, marks: Number(questions[j]?.max_marks || 0) });
                j++;
              }
              items.push({ nodeType: "group", groupType: "OR", pickCount: 1, marks: Number(groupItems[0]?.marks || 0), displayLabel: "Answer any one", question_id: groupItems.map((item: any) => item.questionId || item.id), items: groupItems });
              i = j - 1;
              continue;
            }
            const groupItems: any[] = [{ nodeType: "question", questionId: q.id, marks: Number(q?.max_marks || 0) }];
            let j = i + 1;
            while (j < questions.length && questions[j]?.displayType === "OR") {
              groupItems.push({ nodeType: "question", questionId: questions[j].id, marks: Number(questions[j]?.max_marks || 0) });
              j++;
            }
            if (groupItems.length > 1) {
              items.push({ nodeType: "group", groupType: "OR", pickCount: 1, marks: Number(groupItems[0]?.marks || 0), displayLabel: "Answer any one", question_id: groupItems.map((item: any) => item.questionId || item.id), items: groupItems });
              i = j - 1;
            } else {
              items.push(groupItems[0]);
            }
          }
          const pickCount = items.reduce((acc, it) => acc + (it.nodeType === "group" ? 1 : 1), 0);
          const flatQuestionIds = (questions || []).map((q: any) => q.id);
          const computedTotalMarks = items.reduce((sum: number, it: any) => sum + Number(it?.marks || 0), 0);
          const defaultSectionSubject = sectionSubjects["default-section"];
          return {
            section: "1",
            max_marks: Number((items?.[0]?.marks) || 0),
            no_of_ques: pickCount,
            total_section_marks: computedTotalMarks,
            attemptable_questions: pickCount,
            question_id: flatQuestionIds,
            instruction: "",
            items,
            ...(defaultSectionSubject && { subject_id: defaultSectionSubject }),
          };
        })(),
      ];

    // Calculate total pickable questions across sections (groups count as 1)
    const totalQuestionsAdded = data_json?.reduce((total, section) => {
      const sectionPicks = (section.items || []).reduce((acc: number, it: any) => acc + (it.nodeType === "group" ? 1 : 1), 0);
      return total + sectionPicks;
    }, 0);

    const totalNumberOfQuestions_initial = data_json.reduce((sum, item) => sum + item.no_of_ques, 0);

    // Calculate total exam marks
    // const totalExamMarks = data_json.reduce((total, section) => {
    //   return total + Number(section.total_section_marks || 0);
    // }, 0);

    // Check if this is a multi-subject question paper (sections have different subjects)
    // const hasMultipleSubjects = data_json.some(section => section.subject_id);

    // Get the primary subject_id (from initialState or EditQuestionData)
    // const primarySubjectId = isEditing
    //   ? EditQuestionData?.subject_id
    //   : (initialState?.subject_id && initialState.subject_id.length > 0
    //     ? Number(initialState.subject_id[0])
    //     : undefined);

    // instruction_id is mandatory - ensure it's always present
    const instructionId = instructionList?.[0]?.id || EditQuestionData?.instruction_id;

    // If no instruction exists, don't proceed with save
    if (!instructionId && !silent) {
      toast.error("Please wait for instructions to load or add an instruction first");
      return;
    }

    // For silent auto-save, skip if no instruction_id available yet
    if (!instructionId && silent) {
      return;
    }

    const payload: any = {
      instruction_id: instructionId,
      question_paper_code: qpCode || EditQuestionData?.question_paper_code,
      total_pages: totalPages || EditQuestionData?.total_pages || 1,
      data_json: data_json,
      header_1: headerOne || EditQuestionData?.header_1,
      header_2: headerTwo || EditQuestionData?.header_2,
      total_questions: isDraft ? totalNumberOfQuestions_initial : totalQuestionsAdded,
      is_draft: isDraft,
    };

    // Add subject_id and total_exam_marks for multi-subject format
    // if (hasMultipleSubjects && primarySubjectId) {
    //   payload.subject_id = primarySubjectId;
    //   payload.total_exam_marks = totalExamMarks;
    // }

    // Validate required number of pickable questions (ignore OR items in count)
    if (
      !isDraft &&
      totalQuestionsAdded !== Number(initialState?.total_questions)
    ) {
      if (!isEditing) {
        toast.error(
          `Please add exactly ${initialState?.total_questions} questions. Currently added: ${totalQuestionsAdded}`
        );
        return;
      }
    }

    // Check if the total marks of questions match the max_marks
    // const totalMarks = data_json.reduce((total, section) => {
    //   const sectionKey = `section_${section.section}`;
    //   const sectionMarks = section?.items.reduce((sum, item) => {
    //     return sum + item.marks;
    //   }, 0);
    //   return total + sectionMarks;
    // }, 0);

    // const defaultSectionMarks = sectionQuestions["default-section"]
    //   ? sectionQuestions["default-section"].reduce(
    //     (sum, question) => sum + Number(question?.max_marks),
    //     0
    //   )
    //   : 0;

    // const finalTotalMarks = totalMarks + defaultSectionMarks;

    // if (finalTotalMarks !== Number(initialState?.total_marks)) {
    //   if (!isEditing) {
    //     toast.error(
    //       `Total marks of questions (${finalTotalMarks}) do not match the max marks (${initialState.total_marks}). Please add more questions.`
    //     );
    //     return;
    //   }
    // }
    try {
      const response = await dispatch(
        createManualQuestionPaper({
          id: initialState?.id || EditQuestionData?.id,
          data: payload,
          silent: silent,
        })
      );

      if (createManualQuestionPaper.fulfilled.match(response)) {
        const createdId =
          response.payload?.data?.id ||
          initialState?.id ||
          EditQuestionData?.id;

        // Persist the id locally and on the URL so refresh works.
        if (createdId && !initialState?.id) {
          setInitialState((prev) => ({ ...prev, id: createdId }));
        }
        if (createdId && !id) {
          navigate(
            `/school-admin/question-papers/manual/routing-create-question-paper/${createdId}`,
            { replace: true }
          );
        }

        if (!silent) {
          handleShow();
          await dispatch(
            GridPreviewAsync({
              id: createdId,
            })
          );
        }
      } else {
        toast.error("An error occurred while updating the question paper");
      }
    } catch (error) {
      toast.error("An error occurred while updating the question paper");
    }
  };

  // navigate Question paper //
  const handleQuestionPaper = () => {
    dispatch(resetList());
    dispatch(clearGridPreview());
    navigate("/school-admin/question-papers");
  };


  const handleFinalNext = () => {
    // 1. Clear section data
    setSectionQuestions({});
    setNoSectionQuestions([]);
    setGroupedSections({});
    setSectionSubjects({});

    // 2. Reset form filters
    formik.resetForm();
    setValidState(initialValues);

    // 3. Clear preview header data
    setQpCode("");
    setHeaderOne("");
    setHeaderTwo("");

    // 4. Close preview modal
    handleClose();

    // 5. Redirect to list page (also clears redux)
    handleQuestionPaper();
  };



  useEffect(() => {
    return () => {
      // 1. Clear section data
      setSectionQuestions({});
      setNoSectionQuestions([]);
      setGroupedSections({});
      setSectionSubjects({});

      // 2. Reset form filters
      formik.resetForm();
      setValidState(initialValues);

      // 3. Clear preview header data
      setQpCode("");
      setHeaderOne("");
      setHeaderTwo("");

      // 4. Clear localStorage (grade/subject)
      localStorage.removeItem("grade_id");
      localStorage.removeItem("subject_id");

      // 5. Clear Redux slices
      dispatch(resetList());
      dispatch(clearGridPreview());
    };
  }, []);


  const { values, handleChange } = formik;

  // Handle Fields change logic here //
  const handleFieldChange = (field, value) => {
    const finalValue = typeof value === "object" ? value?.name : value;

    // Handle "-- None --" selection (empty string or null) - clear the field
    if (value === "" || value === null) {
      if (field === "chapter_id") {
        setSelectedChapter(null);
        setSelectedTopic(null);
        formik.setFieldValue(field, null);
        formik.setFieldValue("topic_id", null);
      } else if (field === "topic") {
        setSelectedTopic(null);
        formik.setFieldValue("topic_id", null);
      } else if (field === "subject") {
        formik.setFieldValue("subjectId", null);
        formik.setFieldValue(field, "");
        setSubjectData(null);
      } else {
        formik.setFieldValue(field, "");
      }

      setValidState((prevState) => ({
        ...prevState,
        [field]: "",
      }));

      handleChange({ target: { name: field, value: "" } });
      return;
    }

    if (field === "chapter_id") {
      setSelectedChapter(value);
      setSelectedTopic(null);
      formik.setFieldValue(field, [value?.id]);
      dispatch(TopicListAsync({ chapterId: [value.id] }));
    } else if (field === "topic") {
      setSelectedTopic(value);
      formik.setFieldValue("topic_id", [value.id]);
    } else if (field === "subject") {
      // store subject title for display and id for filtering
      formik.setFieldValue("subjectId", value?.id);
      formik.setFieldValue(field, value?.title);
      setSubjectData(value);
      // Store preview subject only if it's empty (first time selection)
      if (!previewSubjectData) {
        setPreviewSubjectData(value);
      }
      setValidState((prevState) => ({
        ...prevState,
        [field]: value?.title,
      }));
      handleChange({ target: { name: field, value: value?.title } });
      return;
    } else if (field === "questionMarks") {
      // Handle multiple marks selection - value is already an array
      formik.setFieldValue(field, value);
      setValidState((prevState) => ({
        ...prevState,
        [field]: value,
      }));
      handleChange({ target: { name: field, value: value } });
      return;
    } else {
      formik.setFieldValue(field, finalValue);
    }

    setValidState((prevState) => ({
      ...prevState,
      [field]: finalValue,
    }));

    handleChange({ target: { name: field, value: finalValue } });
  };

  useEffect(() => {
    // When opening the page without location state (new paper), clear any previous draft
    if (!EditQuestionData) {
      dispatch(resetList());
      dispatch(clearGridPreview());
    }
    calcRemainingMarks();
    calcRemainingQuestions();
  }, [sectionQuestions]);

  // Auto-save draft (debounced) so refresh restores latest work
  useEffect(() => {
    if (!initialState?.grade_id?.length || !initialState?.subject_id?.length) {
      return;
    }

    // Don't auto-save until instructions are loaded (instruction_id is mandatory)
    const hasInstructionId = instructionList?.[0]?.id || EditQuestionData?.instruction_id;
    if (!hasInstructionId) {
      return;
    }

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      void handleFormSubmit(true, { silent: true });
    }, 1500);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [
    sectionQuestions,
    initialState,
    qpCode,
    headerOne,
    headerTwo,
    EditQuestionData,
  ]);

  useEffect(() => {
    if (!remainingMarks?.length) {
      setRemainingMarks(initialState?.total_marks);
    }
    if (!remainingQuestions?.length) {
      setRemainingQuestions(initialState?.total_questions);
    }
  }, [initialState]);

  // Warn on page refresh/close when there are unsaved changes and clear redux on unmount
  useEffect(() => {
    const hasUnsavedChanges = () => {
      const hasSectionItems = Object.values(sectionQuestions || {}).some(
        (arr: any[]) => Array.isArray(arr) && arr.length > 0
      );
      const hasInitialData = Array.isArray(initialState?.data_json) && initialState.data_json.length > 0;
      return hasSectionItems || hasInitialData;
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      // Clear any in-memory draft when leaving the page
      dispatch(resetList());
      dispatch(clearGridPreview());
    };
  }, [sectionQuestions, initialState?.data_json, dispatch]);

  // handle Drag  logic here with multiple validations///

  const [noSectionQuestions, setNoSectionQuestions] = useState<any[]>([]);

  // Resolve the list currently rendered in the "question" droppable (search vs full list)
  const getRenderedQuestionList = () =>
    searchedQuestions !== null ? searchedQuestions : filteredQuestionList;

  // Get the corresponding index inside the full filtered list by question id
  const getFilteredIndexById = (id: number | string) =>
    filteredQuestionList.findIndex((q: any) => q?.id === id);

  const clearQuestionSelection = () => {
    setSelectedQuestionIds([]);
    setSelectedMarkValue(null);
  };

  const handleQuestionCheckboxToggle = (question: any) => {
    const id = question?.id;
    if (!id && id !== 0) return;
    const normalizedId = Number(id);
    if (Number.isNaN(normalizedId)) return;

    const questionMark = getQuestionEffectiveMarks(question);

    setSelectedQuestionIds((prev) => {
      const alreadySelected = prev.includes(normalizedId);
      if (alreadySelected) {
        const next = prev.filter((qId) => qId !== normalizedId);
        if (next.length === 0) {
          setSelectedMarkValue(null);
        }
        return next;
      }

      if (selectedMarkValue !== null && selectedMarkValue !== questionMark) {
        toastError("select same mark question");
        return prev;
      }

      setSelectedMarkValue(questionMark);
      return [...prev, normalizedId];
    });
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination || !source) {
      return;
    }

    const renderedQuestionList = getRenderedQuestionList();
    const sourceList =
      source.droppableId === "question"
        ? renderedQuestionList
        : source.droppableId === "no_section"
          ? noSectionQuestions
          : sectionQuestions[source.droppableId] || [];
    const itemBeingDragged = sourceList[source.index];
    const draggedId = Number(itemBeingDragged?.id);
    const isSelectionDrag =
      source.droppableId === "question" &&
      selectedQuestionIds.includes(draggedId);

    const movingQuestions = isSelectionDrag
      ? filteredQuestionList.filter((q: any) => selectedQuestionIds.includes(Number(q?.id)))
      : [itemBeingDragged];

    // Ensure all selected questions have the same marks
    const movingMarks = movingQuestions.map((q: any) => getQuestionEffectiveMarks(q));
    const uniqueMarks = new Set(movingMarks);
    if (uniqueMarks.size > 1) {
      toastError("select same mark question");
      return;
    }
    const movingMarkValue = movingMarks[0] ?? getQuestionEffectiveMarks(itemBeingDragged);
    const movingNonOrCount = movingQuestions.filter((q: any) => q?.displayType !== "OR").length;

    // Skip total questions check if moving within the same section
    if (source.droppableId !== destination.droppableId) {
      const totalRequiredQuestions = isEditing
        ? Number(EditQuestionData?.total_questions)
        : Number(initialState?.total_questions);

      const totalCurrentQuestions = Object.values(sectionQuestions)
        .flatMap((section: any) => section)
        .filter((q: any) => q?.displayType !== "OR").length;
      if (
        destination.droppableId !== "question" &&
        totalCurrentQuestions + movingNonOrCount > totalRequiredQuestions
      ) {
        toastError(
          `Cannot add more than ${totalRequiredQuestions} Main questions to the paper.`
        );
        return;
      }

      // Extract section details from droppableId (e.g., "section_A_0" -> letter "A", index 0)
      const sectionMatch = destination.droppableId?.match(/section_([^_]+)_(\d+)/);
      const sectionName = sectionMatch ? sectionMatch[1] : destination.droppableId?.replace("section_", "");
      const sectionIndex = sectionMatch ? parseInt(sectionMatch[2]) : undefined;

      const dataJsonArray = isEditing ? EditQuestionData?.data_json : initialState?.data_json;
      const section = sectionIndex !== undefined
        ? dataJsonArray?.[sectionIndex]
        : dataJsonArray?.find((sec) => sec?.section === sectionName);

      if (section && destination.droppableId !== "question") {
        // Check for question count limit (ignore OR items)
        const maxQuestionsAllowed = section?.no_of_ques;

        if (
          countNonOr(sectionQuestions[destination?.droppableId]) + movingNonOrCount >
          maxQuestionsAllowed
        ) {
          toastError(
            `Cannot add more than ${maxQuestionsAllowed} Main questions to Section ${sectionName}.`
          );
          return;
        }

        // Check for section marks exact match validation
        const sectionMaxMarks = section?.max_marks || 0;
        const currentSectionMarks = sectionQuestions[destination.droppableId]?.max_marks || 0;
        if (movingMarkValue !== sectionMaxMarks) {
          toastError(
            `Cannot add question with ${movingMarkValue} marks to Section ${sectionName}.${" "} Section ${sectionName} requires exactly ${sectionMaxMarks} marks. Current section marks: ${currentSectionMarks}`
          );
          return;
        }
      }
    }

    // Rest of the existing drag and drop logic remains the same
    if (source.droppableId === destination.droppableId) {
      // Handle reorder within the question list separately to respect search view
      if (source.droppableId === "question") {
        const questionList = getRenderedQuestionList();
        const draggedQuestion = questionList[source.index];
        const targetQuestion = questionList[destination.index];

        // Map rendered indexes back to the full filtered list
        const sourceIndexInFiltered = getFilteredIndexById(draggedQuestion?.id);
        const destinationIndexInFiltered = getFilteredIndexById(targetQuestion?.id);

        if (sourceIndexInFiltered === -1 || destinationIndexInFiltered === -1) return;

        const updatedFiltered = reorderList(
          filteredQuestionList,
          sourceIndexInFiltered,
          destinationIndexInFiltered
        );
        setFilteredQuestionList(updatedFiltered);

        // Keep rendered order in sync when search is active
        if (searchedQuestions !== null) {
          const updatedRendered = reorderList(
            questionList,
            source.index,
            destination.index
          );
          setSearchedQuestions(updatedRendered);
        }
      } else {
        const list =
          source.droppableId === "no_section"
            ? noSectionQuestions
            : sectionQuestions[source.droppableId] || [];
        const updatedList = reorderList(list, source.index, destination.index);
        if (source.droppableId === "no_section") {
          setNoSectionQuestions(updatedList);
        } else {
          setSectionQuestions((prev) => ({
            ...prev,
            [source.droppableId]: updatedList,
          }));
        }
      }
    } else {
      const sourceListForUpdate =
        source.droppableId === "question"
          ? filteredQuestionList
          : source.droppableId === "no_section"
            ? noSectionQuestions
            : sectionQuestions[source.droppableId] || [];
      const destinationListForUpdate =
        destination.droppableId === "question"
          ? filteredQuestionList
          : destination.droppableId === "no_section"
            ? noSectionQuestions
            : sectionQuestions[destination.droppableId] || [];

      // Translate indexes when dragging to/from the question list
      const sourceIndex =
        source.droppableId === "question"
          ? getFilteredIndexById(itemBeingDragged?.id)
          : source.index;

      let destinationIndex =
        destination.droppableId === "question"
          ? destinationListForUpdate.length
          : destination.index;

      if (source.droppableId === "question" && sourceIndex === -1) {
        return;
      }

      if (destination.droppableId === "question") {
        // If dropping at a specific position, map it to filtered list
        const renderedList = getRenderedQuestionList();
        const targetQuestion = renderedList[destination.index];
        if (targetQuestion) {
          const mappedIndex = getFilteredIndexById(targetQuestion.id);
          if (mappedIndex !== -1) {
            destinationIndex = mappedIndex;
          }
        }
      }

      if (isSelectionDrag && destination.droppableId !== "question") {
        const selectedIdSet = new Set(
          movingQuestions.map((q: any) => Number(q?.id))
        );
        const updatedSourceList = sourceListForUpdate.filter(
          (q: any) => !selectedIdSet.has(Number(q?.id))
        );

        const destinationClone = Array.from(destinationListForUpdate);
        destinationClone.splice(destinationIndex, 0, ...movingQuestions);

        if (source.droppableId === "question") {
          setFilteredQuestionList(updatedSourceList);
          if (searchedQuestions !== null) {
            setSearchedQuestions((prev) =>
              prev ? prev.filter((q: any) => !selectedIdSet.has(Number(q?.id))) : prev
            );
          }
        } else if (source.droppableId === "no_section") {
          setNoSectionQuestions(updatedSourceList);
        } else {
          setSectionQuestions((prev) => ({
            ...prev,
            [source.droppableId]: updatedSourceList,
          }));
        }

        if (destination.droppableId === "no_section") {
          setNoSectionQuestions(destinationClone);

          // Track subject for default section from the current filter
          if (source.droppableId === "question") {
            const currentSubjectId = subjectData?.id;
            if (currentSubjectId) {
              setSectionSubjects((prev) => ({
                ...prev,
                ["default-section"]: currentSubjectId,
              }));
            }
          }
        } else {
          setSectionQuestions((prev) => ({
            ...prev,
            [destination.droppableId]: destinationClone,
          }));

          // Track subject for the section from the current filter when dragging from question list
          if (source.droppableId === "question") {
            const currentSubjectId = subjectData?.id;
            if (currentSubjectId) {
              setSectionSubjects((prev) => ({
                ...prev,
                [destination.droppableId]: currentSubjectId,
              }));
            }
          }
          // Preserve subject when moving between sections
          else if (source.droppableId.startsWith("section_") || source.droppableId === "no_section") {
            const sourceSubjectId = sectionSubjects[source.droppableId];
            if (sourceSubjectId && !sectionSubjects[destination.droppableId]) {
              setSectionSubjects((prev) => ({
                ...prev,
                [destination.droppableId]: sourceSubjectId,
              }));
            }
          }
        }

        clearQuestionSelection();
        calcRemainingMarks();
        calcRemainingQuestions();

        const updatedQuestionsCount =
          Object.values(sectionQuestions).flat().length + movingQuestions.length;
        setTotalQuestionsCount(updatedQuestionsCount);
        return;
      }

      const result = moveItemBetweenLists(
        sourceListForUpdate,
        destinationListForUpdate,
        sourceIndex,
        destinationIndex
      );

      if (source.droppableId === "question") {
        setFilteredQuestionList(result.sourceList);
        // Also update searchedQuestions if search is active
        if (searchedQuestions !== null) {
          const draggedQuestionId = Number(itemBeingDragged?.id);
          setSearchedQuestions((prev) =>
            prev ? prev.filter((q: any) => Number(q?.id) !== draggedQuestionId) : prev
          );
        }
      } else if (source.droppableId === "no_section") {
        setNoSectionQuestions(result.sourceList);
      } else {
        setSectionQuestions((prev) => ({
          ...prev,
          [source.droppableId]: result.sourceList,
        }));
      }

      if (destination.droppableId === "question") {
        setFilteredQuestionList(result.destinationList);
      } else if (destination.droppableId === "no_section") {
        setNoSectionQuestions(result.destinationList);

        // Track subject for default section from the current filter
        if (source.droppableId === "question") {
          const currentSubjectId = subjectData?.id;
          if (currentSubjectId) {
            setSectionSubjects((prev) => ({
              ...prev,
              ["default-section"]: currentSubjectId,
            }));
          }
        }
      } else {
        setSectionQuestions((prev) => ({
          ...prev,
          [destination.droppableId]: result.destinationList,
        }));

        // Track subject for the section from the current filter when dragging from question list
        if (source.droppableId === "question") {
          const currentSubjectId = subjectData?.id;
          if (currentSubjectId) {
            setSectionSubjects((prev) => ({
              ...prev,
              [destination.droppableId]: currentSubjectId,
            }));
          }
        }
        // Preserve subject when moving between sections
        else if (source.droppableId.startsWith("section_") || source.droppableId === "no_section") {
          const sourceSubjectId = sectionSubjects[source.droppableId];
          if (sourceSubjectId && !sectionSubjects[destination.droppableId]) {
            setSectionSubjects((prev) => ({
              ...prev,
              [destination.droppableId]: sourceSubjectId,
            }));
          }
        }
      }

      calcRemainingMarks();
      calcRemainingQuestions();
    }

    const updatedQuestionsCount =
      Object.values(sectionQuestions).flat().length + movingQuestions.length;
    setTotalQuestionsCount(updatedQuestionsCount);
  };

  useEffect(() => {
    if (!!sectionQuestions["section_1"]) {
      const updatedQuestionsCount = sectionQuestions["section_1"].length;
      setTotalQuestionsCount(updatedQuestionsCount);
    }
  }, [sectionQuestions]);

  const calcRemainingMarks = () => {
    const totalRequiredMarks = isEditing
      ? Number(EditQuestionData?.question_paper_marks)
      : Number(initialState?.total_marks);

    const totalMaxMarks = isEditing
      ? Object.entries(sectionQuestions).reduce(
        (total, [sectionKey, questions]) => {
          return (
            total +
            questions.reduce((sectionTotal, question) => {
              return sectionTotal + Number(question?.max_marks);
            }, 0)
          );
        },
        0
      )
      : Object.values(sectionQuestions)
        .flatMap((section) => section)
        .reduce((total, question) => total + getQuestionEffectiveMarks(question), 0);

    setRemainingMarks(totalRequiredMarks - totalMaxMarks);
  };

  const calcRemainingQuestions = () => {
    const totalRequiredQuestions = isEditing
      ? Number(EditQuestionData?.total_questions)
      : Number(initialState?.total_questions);

    const totalCurrentPickable = Object.values(sectionQuestions)
      .flatMap((section: any) => section as any[])
      .filter((q: any) => q?.displayType !== "OR").length;

    const remaining = totalRequiredQuestions - totalCurrentPickable;
    setRemainingQuestions(remaining > 0 ? remaining : 0);
  };
  const reorderList = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const moveItemBetweenLists = (
    sourceList,
    destinationList,
    sourceIndex,
    destinationIndex
  ) => {
    const sourceClone = Array.from(sourceList);
    const destClone = Array.from(destinationList);
    const [removed] = sourceClone.splice(sourceIndex, 1);

    destClone.splice(destinationIndex, 0, removed);

    return {
      sourceList: sourceClone,
      destinationList: destClone,
    };
  };

  // Helper function to get subject name from ID
  const getSubjectNameById = (subjectId: number | string) => {
    if (!subjectId) return null;
    const subject = subjectList?.find((s: any) => s.id === Number(subjectId));
    return subject?.title || subjectId;
  };

  const countNonOr = (list: any[] = []) => list.filter((q: any) => q?.displayType !== "OR").length;

  // List data here //
  const instructionList = useTypedSelector(DisplayInstructionDataList);
  const classList = useTypedSelector(DisplayClassesDataList);
  const subjectList = useTypedSelector(DisplaySubjectDataList);

  useEffect(() => {
    const foundClass = classList?.find((item: any) => item?.id === initialState?.grade_id[0]);
    const foundSubject = subjectList?.find((item: any) => item.id === initialState?.subject_id[0]);

    setClassData(foundClass);
    setSubjectData(foundSubject);

    // Set preview subject data if not already set (initial load only)
    if (foundSubject) {
      setPreviewSubjectData((prev) => prev || foundSubject);
    }
  }, [classList, subjectList]);

  // instructions show based on grade and subject //
  useEffect(() => {
    dispatch(
      InstructionListAsync({
        gradeId: initialState?.grade_id,
        subjectId: initialState?.subject_id,
      })
    );
    dispatch(ClassesListAsync({}));
    dispatch(SubjectListAsync({ gradeId: initialState?.grade_id }));
  }, [dispatch, initialState?.grade_id, initialState?.subject_id]);

  // Calculate total pages
  const totalPages = Math.ceil(
    filteredQuestionList.length / QUESTIONS_PER_PAGE
  );

  // Handle Q_ID search - only searches locally (no API call)
  const handleQIdSearchLocal = useCallback(() => {
    if (!qIdSearchInput.trim()) {
      // Clear search if input is empty
      setSearchedQuestions(null);
      setSearchMessage("");
      setIsApiSearchActive(false);
      setAllSearchedQuestions([]);
      return;
    }

    // Extract comma-separated IDs
    const searchIds = qIdSearchInput
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id !== "")
      .map((id) => {
        // Remove "Q" prefix if present and convert to number
        const numId = id.replace(/^Q/i, "").trim();
        return isNaN(Number(numId)) ? null : Number(numId);
      })
      .filter((id) => id !== null) as number[];

    if (searchIds.length === 0) {
      setSearchMessage("Please enter valid Q_IDs (comma-separated numbers)");
      setSearchedQuestions([]);
      setIsApiSearchActive(false);
      setAllSearchedQuestions([]);
      return;
    }

    // Search only in filteredQuestionList (local)
    const foundQuestions = filteredQuestionList.filter((question) =>
      searchIds.includes(Number(question.id))
    );

    // Order questions according to the search sequence
    const orderedFoundQuestions = searchIds
      .map(searchId => foundQuestions.find(q => Number(q.id) === searchId))
      .filter(q => q !== undefined);

    const foundIds = orderedFoundQuestions.map((q) => q.id);
    const notFoundIds = searchIds.filter((id) => !foundIds.includes(id));

    if (orderedFoundQuestions.length === 0) {
      setSearchMessage(`No questions found locally. Click "Search" to search in API.`);
      setSearchedQuestions([]);
      setIsApiSearchActive(false);
      setAllSearchedQuestions([]);
    } else if (notFoundIds.length > 0) {
      setSearchMessage(
        `Found ${orderedFoundQuestions.length} question(s) locally. ${notFoundIds.length} not found. Click "Search" to search in API.`
      );
      setSearchedQuestions(orderedFoundQuestions);
      setIsApiSearchActive(true); // CRITICAL FIX: Set to true so displayType preservation works
      setAllSearchedQuestions(orderedFoundQuestions); // CRITICAL FIX: Store local results
    } else {
      setSearchMessage(`Found ${orderedFoundQuestions.length} question(s) locally.`);
      setSearchedQuestions(orderedFoundQuestions);
      setIsApiSearchActive(true); // CRITICAL FIX: Set to true so displayType preservation works
      setAllSearchedQuestions(orderedFoundQuestions); // CRITICAL FIX: Store local results
    }
  }, [qIdSearchInput, filteredQuestionList]);

  // Handle Q_ID search with API call - triggered by button click
  const handleQIdSearchWithAPI = useCallback(async () => {
    if (!qIdSearchInput.trim()) {
      setSearchMessage("Please enter Q_IDs to search");
      return;
    }

    // Extract comma-separated IDs
    const searchIds = qIdSearchInput
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id !== "")
      .map((id) => {
        // Remove "Q" prefix if present and convert to number
        const numId = id.replace(/^Q/i, "").trim();
        return isNaN(Number(numId)) ? null : Number(numId);
      })
      .filter((id) => id !== null) as number[];

    if (searchIds.length === 0) {
      setSearchMessage("Please enter valid Q_IDs (comma-separated numbers)");
      setSearchedQuestions([]);
      return;
    }

    // Check if any searched IDs are already in the paper
    const idsAlreadyInPaper = searchIds.filter((id) => existingQuestionIds.has(id));
    if (idsAlreadyInPaper.length > 0) {
      setSearchMessage(
        `Q_IDs already in question paper: ${idsAlreadyInPaper.join(", ")}. ${idsAlreadyInPaper.length < searchIds.length ? "Searching for remaining IDs..." : ""
        }`
      );
      if (idsAlreadyInPaper.length === searchIds.length) {
        setSearchedQuestions([]);
        return;
      }
    }

    // Filter out IDs already in paper before searching
    const searchIdsToFind = searchIds.filter((id) => !existingQuestionIds.has(id));

    // First, search locally
    const foundQuestions = filteredQuestionList.filter((question) =>
      searchIdsToFind.includes(Number(question.id))
    );

    const foundIds = foundQuestions.map((q) => q.id);
    const notFoundIds = searchIdsToFind.filter((id) => !foundIds.includes(id));

    // If all found locally, no need to call API
    if (notFoundIds.length === 0) {
      // Order questions according to search sequence
      const orderedFoundQuestions = searchIdsToFind
        .map(searchId => foundQuestions.find(q => Number(q.id) === searchId))
        .filter(q => q !== undefined);

      const msg = idsAlreadyInPaper.length > 0
        ? `Found ${orderedFoundQuestions.length} question(s) locally. Q_IDs already in paper: ${idsAlreadyInPaper.join(", ")}`
        : `Found ${orderedFoundQuestions.length} question(s) locally.`;
      setSearchMessage(msg);
      setSearchedQuestions(orderedFoundQuestions);
      return;
    }

    // Search API for missing IDs
    setSearchMessage("Searching API for questions...");
    try {
      // Build payload with current filters and the missing IDs
      const payload: any = {
        id: notFoundIds, // Array of IDs to search
        // ...(subjectData?.id && { subjectId: [Number(subjectData.id)] }),
        // ...(classData?.id && { gradeId: [Number(classData.id)] }),
      };

      const response = await dispatch(QuestionListAsync({
        ...payload,
        // page: 1,
        // pageSize: 10, // Large page size to get all matching
      }));

      if (response?.meta?.requestStatus === "fulfilled") {
        const { questions } = response.payload;
        const apiFoundQuestions = (questions || []).filter((q: any) =>
          notFoundIds.includes(Number(q.id))
        );

        // Combine local and API results
        const allFoundQuestions = [...foundQuestions, ...apiFoundQuestions.map((q: any) => ({
          ...q,
          displayType: q?.displayType || "Main",
        }))];

        // Order questions according to the search sequence (searchIdsToFind order)
        const orderedQuestions = searchIdsToFind
          .map(searchId => allFoundQuestions.find(q => Number(q.id) === searchId))
          .filter(q => q !== undefined);

        const stillNotFoundIds = notFoundIds.filter((id) =>
          !apiFoundQuestions.some((q: any) => Number(q.id) === id)
        );

        let msg = "";
        if (stillNotFoundIds.length > 0) {
          msg = `Found ${orderedQuestions.length} question(s). Q_IDs not found: ${stillNotFoundIds.join(", ")}`;
        } else {
          msg = `Found ${orderedQuestions.length} question(s)`;
        }

        if (idsAlreadyInPaper.length > 0) {
          msg += `. Q_IDs already in paper: ${idsAlreadyInPaper.join(", ")}`;
        }

        setSearchMessage(msg);
        setSearchedQuestions(orderedQuestions);
        setAllSearchedQuestions(orderedQuestions); // Keep track of all searched questions
        setIsApiSearchActive(true); // Set flag to prevent local search override
      } else {
        // If API call fails, just show local results
        if (foundQuestions.length > 0) {
          setSearchMessage(
            `Found ${foundQuestions.length} question(s) locally. Q_IDs not found: ${notFoundIds.join(", ")}`
          );
          setSearchedQuestions(foundQuestions);
        } else {
          setSearchMessage(`No questions found with the provided Q_IDs: ${qIdSearchInput}`);
          setSearchedQuestions([]);
        }
      }
    } catch (error) {
      console.error("Error fetching questions from API:", error);
      toast.error("Failed to search questions in API");
      // Fallback to local results
      if (foundQuestions.length > 0) {
        setSearchMessage(
          `Found ${foundQuestions.length} question(s) locally. Q_IDs not found: ${notFoundIds.join(", ")}`
        );
        setSearchedQuestions(foundQuestions);
      } else {
        setSearchMessage(`No questions found with the provided Q_IDs: ${qIdSearchInput}`);
        setSearchedQuestions([]);
      }
    }
  }, [qIdSearchInput, filteredQuestionList, subjectData, classData, dispatch, existingQuestionIds]);


  // Get all questions (no pagination)
  // Use searched questions if available, otherwise use filtered list
  const currentPageQuestions = searchedQuestions !== null ? searchedQuestions : filteredQuestionList;

  // Re-run local search when filteredQuestionList changes (if search is active)
  // This only searches locally, no API calls
  // Skip if API search is active to prevent overriding API results
  useEffect(() => {
    if (isApiSearchActive) {
      // Don't override API search results with local search
      return;
    }

    if (qIdSearchInput.trim()) {
      handleQIdSearchLocal();
    } else {
      setSearchedQuestions(null);
      setSearchMessage("");
    }
  }, [filteredQuestionList, qIdSearchInput, handleQIdSearchLocal, isApiSearchActive]);

  const EditHeaderData = {
    header_1: EditQuestionData?.header_1,
    header_2: EditQuestionData?.header_2,
  };

  const { id } = useParams();

  useEffect(() => {
    if (id) {
      dispatch(QuestionPaperPreviewAsync({ id }));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (EditQuestionData?.question_paper_code) {
      setQpCode(EditQuestionData.question_paper_code);
    }
  }, [EditQuestionData]);

  // Add Question Paper Modal //

  const [editorQuestionHtml, setEditorQuestionHtml] = useState("");
  const [editorAnswerHtml, setEditorAnswerHtml] = useState("");
  const [optionEditors, setOptionEditors] = useState([]);
  const [selectedAnswers, setSelectedAnswers] = useState([]);

  // const handleOpenEditor = () => {
  //   setOptionEditors((prevOptions) => [...prevOptions, ""]);
  // };

  // while add question then options editor logic here //
  const handleOptionEditorChange = (index, data) => {
    const newOptions = [...optionEditors];
    newOptions[index] = { ...newOptions[index], data };
    setOptionEditors(newOptions);
  };

  // while add question then options editor remove logic here //
  const handleRemoveEditor = (index) => {
    setOptionEditors((prevOptions) =>
      prevOptions.filter((_, i) => i !== index)
    );
  };

  // checkbox showing with particular options while add question //
  const handleCheckboxChange = (index) => {
    const newSelectedAnswers = [...selectedAnswers];
    const answerIndex = newSelectedAnswers.indexOf(index + 1);
    if (answerIndex > -1) {
      newSelectedAnswers.splice(answerIndex, 1);
    } else {
      newSelectedAnswers.push(index + 1);
    }
    setSelectedAnswers(newSelectedAnswers);
  };

  // const handleQuestionEditor = (event) => {
  //   const value = event.editor.getData();
  //   setEditorQuestionHtml(value);
  // };

  // const handleAnswerEditor = (event) => {
  //   const value = event.editor.getData();
  //   setEditorAnswerHtml(value);
  // };

  // Add Question logic in Question Accordian here //
  // const handleAddQuestion = async (e) => {
  //   e.preventDefault();

  //   const options = optionEditors?.map((option, index) => ({
  //     data: option,
  //     option: index + 1,
  //   }));

  //   let { chapter_id, topic_id } = values;

  //   const gradeId = isEditing
  //     ? EditQuestionData?.grade_id
  //     : initialState?.grade_id;
  //   const subjectId = isEditing
  //     ? EditQuestionData?.subject_id
  //     : initialState?.subject_id;

  //   const payload = {
  //     ...(topic_id && { topic_id: [Number(topic_id)] }),
  //     ...(selectedChapter && { chapter_id: [Number(selectedChapter.id)] }),
  //     question_text: editorQuestionHtml,
  //     type: validState?.questionType,
  //     grade_id: gradeId != null ? [Number(gradeId)] : [],
  //     subject_id: subjectId != null ? [Number(subjectId)] : [],
  //     max_marks: validState?.questionMarks,
  //     level: validState?.level,
  //     skill: validState?.skill,
  //     answer_text:
  //       validState?.questionType === "Multiple-choice" ||
  //         validState?.questionType === "True / False"
  //         ? selectedAnswers.join(",")
  //         : editorAnswerHtml,
  //     question_options: options,
  //   };

  //   try {
  //     await dispatch(CreateQuestionAsync(payload));
  //     handleNewQuestionClose();
  //     let { questionMarks, topic_id, skill, questionType, level } = values;

  //     const payloads = {
  //       ...(topic_id && { topicId: [Number(topic_id)] }),
  //       ...(skill && { skillId: [skill] }),
  //       ...(level && { levelId: [level] }),
  //       ...(questionMarks?.length && { marksId: questionMarks }),
  //       ...(questionType && { typeId: [questionType] }),
  //       ...(selectedChapter && { chapterId: [Number(selectedChapter.id)] }),
  //     };
  //     try {
  //       await dispatch(QuestionListAsync(payloads));
  //     } catch {
  //       // toast.error("Failed to add question");
  //     }
  //   } catch (error) {
  //     toast.error("Failed to add question");
  //   }
  // };

  // Convert Indexing into letter(A,B,C,D) //
  const indexToLetter = (index) => {
    return String.fromCharCode(65 + index);
  };

  // handle back button //
  // const handleBack = () => {
  //   navigate("/school-admin/question-papers");
  // };

  // Remove html tags //
  const stripPTags = (html) => {
    if (!html) return "";

    let temp = document.createElement("div");
    temp.innerHTML = html;

    return temp.textContent || temp.innerText || "";
  };

  const handleQuestionDisplayTypeChange = (questionId: number, value: "Main" | "OR") => {
    setFilteredQuestionList((prev: any[]) =>
      prev.map((q: any) => (q.id === questionId ? { ...q, displayType: value } : q))
    );
    // Also update searchedQuestions if search is active
    if (searchedQuestions !== null) {
      setSearchedQuestions((prev: any[]) =>
        prev ? prev.map((q: any) => (q.id === questionId ? { ...q, displayType: value } : q)) : prev
      );
    }
    // Also update allSearchedQuestions to persist the change in the source data
    if (isApiSearchActive && allSearchedQuestions.length > 0) {
      setAllSearchedQuestions((prev: any[]) =>
        prev.map((q: any) => (q.id === questionId ? { ...q, displayType: value } : q))
      );
    }
  };

  // tsx here //

  return (
    <>
      <div className="container-fluid">
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="row">
            <div className="col-12 col-lg-6">
              <div className="left-create-area">
                <Accordion defaultActiveKey="0" activeKey={accordionIndex}>
                  {/* Filter accordian here */}
                  <Accordion.Item eventKey="0" className="mb-2 border-0">
                    <Accordion.Header onClick={() => handleAccordionPanel("0")}>
                      FILTERS
                    </Accordion.Header>
                    <div className="filter-floating-box rounded-md">
                      <Accordion.Body>
                        <form
                          action=""
                          className="horizaontal-form question-form"
                          onSubmit={formik.handleSubmit}
                        >
                          <div className="row">
                            <div className="col-12 col-md-6 mb-2">
                              <div className="form-group">
                                <label>Question Type</label>
                                <Listbox
                                  value={validState?.questionType}
                                  onChange={(val) =>
                                    handleFieldChange("questionType", val)
                                  }
                                >
                                  <div className="relative mt-1">
                                    <Listbox.Button className="relative text-left">
                                      <span className="block truncate">
                                        {validState?.questionType ||
                                          "Select Type"}
                                      </span>
                                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                        <ChevronUpDownIcon
                                          className="h-5 w-5 text-gray-400"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    </Listbox.Button>
                                    <Transition
                                      as={Fragment}
                                      leave="transition ease-in duration-100"
                                      leaveFrom="opacity-100"
                                      leaveTo="opacity-0"
                                    >
                                      <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                        <Listbox.Option
                                          key="none"
                                          className={({ active }) =>
                                            `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                              ? "bg-orange-100 text-orange-700"
                                              : "text-gray-900"
                                            }`
                                          }
                                          value=""
                                        >
                                          {({ selected }) => (
                                            <>
                                              <span
                                                className={`block truncate ${selected
                                                  ? "font-medium"
                                                  : "font-normal"
                                                  }`}
                                              >
                                                Select Type
                                              </span>
                                              {selected ? (
                                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                  <CheckIcon
                                                    className="h-5 w-5"
                                                    aria-hidden="true"
                                                  />
                                                </span>
                                              ) : null}
                                            </>
                                          )}
                                        </Listbox.Option>
                                        {questionType?.map(
                                          (quetionType, quetionTypeIdx) => (
                                            <Listbox.Option
                                              key={quetionTypeIdx}
                                              className={({ active }) =>
                                                `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                                  ? "bg-orange-100 text-orange-700"
                                                  : "text-gray-900"
                                                }`
                                              }
                                              value={quetionType}
                                            >
                                              {({ selected }) => (
                                                <>
                                                  <span
                                                    className={`block truncate ${selected
                                                      ? "font-medium"
                                                      : "font-normal"
                                                      }`}
                                                  >
                                                    {quetionType?.name}
                                                  </span>
                                                  {selected ? (
                                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                      <CheckIcon
                                                        className="h-5 w-5"
                                                        aria-hidden="true"
                                                      />
                                                    </span>
                                                  ) : null}
                                                </>
                                              )}
                                            </Listbox.Option>
                                          )
                                        )}
                                      </Listbox.Options>
                                    </Transition>
                                  </div>
                                </Listbox>
                              </div>
                            </div>

                            <div className="col-12 col-md-6 mb-2">
                              <div className="form-group">
                                <label>Difficulty Level</label>
                                <Listbox
                                  value={validState?.level}
                                  onChange={(val) =>
                                    handleFieldChange("level", val)
                                  }
                                >
                                  <div className="relative mt-1">
                                    <Listbox.Button className="relative text-left">
                                      <span className="block truncate">
                                        {validState?.level || "Select Level"}
                                      </span>
                                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                        <ChevronUpDownIcon
                                          className="h-5 w-5 text-gray-400"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    </Listbox.Button>
                                    <Transition
                                      as={Fragment}
                                      leave="transition ease-in duration-100"
                                      leaveFrom="opacity-100"
                                      leaveTo="opacity-0"
                                    >
                                      <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                        <Listbox.Option
                                          key="none"
                                          className={({ active }) =>
                                            `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                              ? "bg-orange-100 text-orange-700"
                                              : "text-gray-900"
                                            }`
                                          }
                                          value=""
                                        >
                                          {({ selected }) => (
                                            <>
                                              <span
                                                className={`block truncate ${selected
                                                  ? "font-medium"
                                                  : "font-normal"
                                                  }`}
                                              >
                                                Select Level
                                              </span>
                                              {selected ? (
                                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                  <CheckIcon
                                                    className="h-5 w-5"
                                                    aria-hidden="true"
                                                  />
                                                </span>
                                              ) : null}
                                            </>
                                          )}
                                        </Listbox.Option>
                                        {level?.map(
                                          (quetionLevel, quetionLevelIdx) => (
                                            <Listbox.Option
                                              key={quetionLevelIdx}
                                              className={({ active }) =>
                                                `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                                  ? "bg-orange-100 text-orange-700"
                                                  : "text-gray-900"
                                                }`
                                              }
                                              value={quetionLevel}
                                            >
                                              {({ selected }) => (
                                                <>
                                                  <span
                                                    className={`block truncate ${selected
                                                      ? "font-medium"
                                                      : "font-normal"
                                                      }`}
                                                  >
                                                    {quetionLevel?.name}
                                                  </span>
                                                  {selected ? (
                                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                      <CheckIcon
                                                        className="h-5 w-5"
                                                        aria-hidden="true"
                                                      />
                                                    </span>
                                                  ) : null}
                                                </>
                                              )}
                                            </Listbox.Option>
                                          )
                                        )}
                                      </Listbox.Options>
                                    </Transition>
                                  </div>
                                </Listbox>
                              </div>
                            </div>

                            <div className="col-12 col-md-6 mb-2">
                              <div className="form-group">
                                <label>Competency/Skill Level</label>
                                <Listbox
                                  value={validState.skill}
                                  onChange={(val) =>
                                    handleFieldChange("skill", val)
                                  }
                                >
                                  <div className="relative mt-1">
                                    <Listbox.Button className="relative text-left">
                                      <span className="block truncate">
                                        {validState?.skill || "Select Skills"}
                                      </span>
                                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                        <ChevronUpDownIcon
                                          className="h-5 w-5 text-gray-400"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    </Listbox.Button>
                                    <Transition
                                      as={Fragment}
                                      leave="transition ease-in duration-100"
                                      leaveFrom="opacity-100"
                                      leaveTo="opacity-0"
                                    >
                                      <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                        <Listbox.Option
                                          key="none"
                                          className={({ active }) =>
                                            `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                              ? "bg-orange-100 text-orange-700"
                                              : "text-gray-900"
                                            }`
                                          }
                                          value=""
                                        >
                                          {({ selected }) => (
                                            <>
                                              <span
                                                className={`block truncate ${selected
                                                  ? "font-medium"
                                                  : "font-normal"
                                                  }`}
                                              >
                                                Select Skills
                                              </span>
                                              {selected ? (
                                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                  <CheckIcon
                                                    className="h-5 w-5"
                                                    aria-hidden="true"
                                                  />
                                                </span>
                                              ) : null}
                                            </>
                                          )}
                                        </Listbox.Option>
                                        {skill?.map((skill, skillIdx) => (
                                          <Listbox.Option
                                            key={skillIdx}
                                            className={({ active }) =>
                                              `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                                ? "bg-orange-100 text-orange-700"
                                                : "text-gray-900"
                                              }`
                                            }
                                            value={skill}
                                          >
                                            {({ selected }) => (
                                              <>
                                                <span
                                                  className={`block truncate ${selected
                                                    ? "font-medium"
                                                    : "font-normal"
                                                    }`}
                                                >
                                                  {skill?.name}
                                                </span>
                                                {selected ? (
                                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                    <CheckIcon
                                                      className="h-5 w-5"
                                                      aria-hidden="true"
                                                    />
                                                  </span>
                                                ) : null}
                                              </>
                                            )}
                                          </Listbox.Option>
                                        ))}
                                      </Listbox.Options>
                                    </Transition>
                                  </div>
                                </Listbox>
                              </div>
                            </div>

                            <div className="col-12 col-md-6 mb-2">
                              <div className="form-group">
                                <label>
                                  Marks<span className="form_required">*</span>
                                </label>
                                <Listbox
                                  multiple
                                  value={validState?.questionMarks || []}
                                  onChange={(vals) =>
                                    handleFieldChange("questionMarks", vals)
                                  }
                                >
                                  <div className="relative mt-1">
                                    <Listbox.Button className="relative text-left">
                                      <span className="block truncate">
                                        {validState?.questionMarks?.length
                                          ? validState.questionMarks.join(", ")
                                          : "Select Marks"}
                                      </span>
                                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                        <ChevronUpDownIcon
                                          className="h-5 w-5 text-gray-400"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    </Listbox.Button>
                                    <Transition
                                      as={Fragment}
                                      leave="transition ease-in duration-100"
                                      leaveFrom="opacity-100"
                                      leaveTo="opacity-0"
                                    >
                                      <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                        {questionMarks?.map(
                                          (quetionMarks, quetionMarksIdx) => (
                                            <Listbox.Option
                                              key={quetionMarksIdx}
                                              className={({ active }) =>
                                                `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                                  ? "bg-orange-100 text-orange-700"
                                                  : "text-gray-900"
                                                }`
                                              }
                                              value={quetionMarks.name}
                                            >
                                              {({ selected }) => (
                                                <>
                                                  <span
                                                    className={`block truncate ${selected
                                                      ? "font-medium"
                                                      : "font-normal"
                                                      }`}
                                                  >
                                                    {quetionMarks?.name}
                                                  </span>
                                                  {selected ? (
                                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                      <CheckIcon
                                                        className="h-5 w-5"
                                                        aria-hidden="true"
                                                      />
                                                    </span>
                                                  ) : null}
                                                </>
                                              )}
                                            </Listbox.Option>
                                          )
                                        )}
                                      </Listbox.Options>
                                    </Transition>
                                  </div>
                                </Listbox>
                                {formik.touched.questionMarks &&
                                  formik.errors.questionMarks &&
                                  (!validState.questionMarks || validState.questionMarks.length === 0) && (
                                    <div className="error">
                                      {formik.errors.questionMarks}
                                    </div>
                                  )}
                              </div>
                            </div>

                            {/* chapter fields */}

                            <div className="col-12 col-md-6 mb-2">
                              <div className="form-group">
                                <label>Chapter</label>
                                <Listbox
                                  value={validState?.chapter_id || []}
                                  onChange={(chapter: any) => {
                                    handleFieldChange("chapter_id", chapter);
                                  }}
                                >
                                  <div className="relative">
                                    <Listbox.Button className="relative text-left">
                                      <span className="block truncate">
                                        {selectedChapter?.chapter ||
                                          "Select Chapter"}
                                      </span>
                                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                        <ChevronUpDownIcon
                                          className="h-5 w-5 text-gray-400"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    </Listbox.Button>
                                    <Transition
                                      as={Fragment}
                                      leave="transition ease-in duration-100"
                                      leaveFrom="opacity-100"
                                      leaveTo="opacity-0"
                                    >
                                      <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                        <Listbox.Option
                                          key="none"
                                          className={({ active }) =>
                                            `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                              ? "bg-orange-100 text-orange-700"
                                              : "text-gray-900"
                                            }`
                                          }
                                          value={null}
                                        >
                                          {({ selected }) => (
                                            <>
                                              <span
                                                className={`block truncate ${selected
                                                  ? "font-medium"
                                                  : "font-normal"
                                                  }`}
                                              >
                                                Select Chapter
                                              </span>
                                              {selected ? (
                                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                  <CheckIcon
                                                    className="h-5 w-5"
                                                    aria-hidden="true"
                                                  />
                                                </span>
                                              ) : null}
                                            </>
                                          )}
                                        </Listbox.Option>
                                        {chaptersDataList?.length > 0 ? (
                                          chaptersDataList?.map(
                                            (chapter, idx) => (
                                              <Listbox.Option
                                                key={idx}
                                                className={({ active }) =>
                                                  `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                                    ? "bg-orange-100 text-orange-700"
                                                    : "text-gray-900"
                                                  }`
                                                }
                                                value={chapter}
                                              >
                                                {({ selected }) => (
                                                  <>
                                                    <span
                                                      className={`block truncate ${selected
                                                        ? "font-medium"
                                                        : "font-normal"
                                                        }`}
                                                    >
                                                      {chapter?.chapter}
                                                    </span>
                                                    {selected && (
                                                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                        <CheckIcon
                                                          className="h-5 w-5"
                                                          aria-hidden="true"
                                                        />
                                                      </span>
                                                    )}
                                                  </>
                                                )}
                                              </Listbox.Option>
                                            )
                                          )
                                        ) : (
                                          <div className="relative cursor-default select-none py-2 pl-10 pr-4 text-gray-700">
                                            No Values Found
                                          </div>
                                        )}
                                      </Listbox.Options>
                                    </Transition>
                                  </div>
                                </Listbox>
                              </div>
                            </div>

                            <div className="col-12 col-md-6 mb-2">
                              {selectedChapter && (
                                <div className="form-group">
                                  <label>Topic</label>
                                  <Listbox
                                    value={selectedTopic || {}}
                                    onChange={(val) =>
                                      handleFieldChange("topic", val)
                                    }
                                  >
                                    <div className="relative mt-1">
                                      <Listbox.Button className="relative text-left">
                                        <span className="block truncate">
                                          {selectedTopic?.title ||
                                            "Select Topic"}
                                        </span>
                                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                          <ChevronUpDownIcon
                                            className="h-5 w-5 text-gray-400"
                                            aria-hidden="true"
                                          />
                                        </span>
                                      </Listbox.Button>
                                      <Transition
                                        as={Fragment}
                                        leave="transition ease-in duration-100"
                                        leaveFrom="opacity-100"
                                        leaveTo="opacity-0"
                                      >
                                        <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                          {TopicDataList?.length > 0 ? (
                                            TopicDataList?.map(
                                              (topic, topicIdx) => (
                                                <Listbox.Option
                                                  key={topicIdx}
                                                  className={({ active }) =>
                                                    `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                                      ? "bg-orange-100 text-orange-700"
                                                      : "text-gray-900"
                                                    }`
                                                  }
                                                  value={topic}
                                                >
                                                  {({ selected }) => (
                                                    <>
                                                      <span
                                                        className={`block truncate ${selected
                                                          ? "font-medium"
                                                          : "font-normal"
                                                          }`}
                                                      >
                                                        {topic?.title}
                                                      </span>
                                                      {selected && (
                                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                          <CheckIcon
                                                            className="h-5 w-5"
                                                            aria-hidden="true"
                                                          />
                                                        </span>
                                                      )}
                                                    </>
                                                  )}
                                                </Listbox.Option>
                                              )
                                            )
                                          ) : (
                                            <div className="relative cursor-default select-none py-2 pl-10 pr-4 text-gray-700">
                                              No Values Found
                                            </div>
                                          )}
                                        </Listbox.Options>
                                      </Transition>
                                    </div>
                                  </Listbox>
                                </div>
                              )}
                            </div>
                            <div className="col-12 col-md-6 mb-2">
                              <div className="form-group">
                                <label>Subject list</label>
                                <Listbox
                                  value={subjectData}
                                  onChange={(val) =>
                                    handleFieldChange("subject", val)
                                  }
                                >
                                  <div className="relative mt-1">
                                    <Listbox.Button className="relative text-left">
                                      <span className="flex items-center justify-between w-full block truncate">
                                        {(() => {
                                          const selected = subjectData || validState?.subjectData;
                                          if (!selected) {
                                            return (
                                              <span className="block truncate">
                                                {validState?.subject || "Select Subject"}
                                              </span>
                                            );
                                          }
                                          const isSub = !!selected?.parent_id;
                                          return (
                                            <>
                                              <span className="mr-2">
                                                {selected?.title || validState?.subject}
                                              </span>
                                              <span
                                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${isSub
                                                  ? "bg-blue-100 text-blue-700"
                                                  : "bg-green-100 text-green-700"
                                                  }`}
                                              >
                                                {isSub ? "Sub" : "Main"}
                                              </span>
                                            </>
                                          );
                                        })()}
                                      </span>
                                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                        <ChevronUpDownIcon
                                          className="h-5 w-5 text-gray-400"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    </Listbox.Button>
                                    <Transition
                                      as={Fragment}
                                      leave="transition ease-in duration-100"
                                      leaveFrom="opacity-100"
                                      leaveTo="opacity-0"
                                    >
                                      <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                        <Listbox.Option
                                          key="none"
                                          className={({ active }) =>
                                            `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                              ? "bg-orange-100 text-orange-700"
                                              : "text-gray-900"
                                            }`
                                          }
                                          value={null}
                                        >
                                          {({ selected }) => (
                                            <>
                                              <span
                                                className={`block truncate ${selected
                                                  ? "font-medium"
                                                  : "font-normal"
                                                  }`}
                                              >
                                                Select Subject
                                              </span>
                                              {selected ? (
                                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                  <CheckIcon
                                                    className="h-5 w-5"
                                                    aria-hidden="true"
                                                  />
                                                </span>
                                              ) : null}
                                            </>
                                          )}
                                        </Listbox.Option>
                                        {subjectList?.map((subject: any, subjectIdx: number) => (
                                          <Listbox.Option
                                            key={subjectIdx}
                                            className={({ active }) =>
                                              `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                                ? "bg-orange-100 text-orange-700"
                                                : "text-gray-900"
                                              }`
                                            }
                                            value={subject}
                                          >
                                            {({ selected }) => (
                                              <>
                                                <span
                                                  className={`flex items-center justify-between w-full block truncate ${selected ? "font-medium" : "font-normal"
                                                    }`}
                                                >
                                                  <span className="mr-2">{subject?.title}</span>
                                                  <span
                                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${subject?.parent_id
                                                      ? "bg-blue-100 text-blue-700"
                                                      : "bg-green-100 text-green-700"
                                                      }`}
                                                  >
                                                    {subject?.parent_id ? "Sub" : "Main"}
                                                  </span>
                                                </span>
                                                {selected ? (
                                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                    <CheckIcon
                                                      className="h-5 w-5"
                                                      aria-hidden="true"
                                                    />
                                                  </span>
                                                ) : null}
                                              </>
                                            )}
                                          </Listbox.Option>
                                        ))}
                                      </Listbox.Options>
                                    </Transition>
                                  </div>
                                </Listbox>
                              </div>
                            </div>

                            {/* This is info table for question availability*/}
                            <MarksToolTipsComponents gradeId={classData?.id} subjectId={subjectData?.id} loaderReferenceFromToolComponentInfoLoader={loaderReferenceFromToolComponentInfoLoader} setLoaderReferenceFromToolComponentInfoLoader={setLoaderReferenceFromToolComponentInfoLoader} />
                            <div className="col-12 col-md-12 mt-2">
                              <div className="form-group">
                                {
                                  loaderReferenceFromToolComponentInfoLoader ?
                                    <button className="custom-active-button w-1/2 block mx-auto rounded-md flex items-center justify-center h-12">
                                      <Loader customClassName="h-full w-full flex items-center justify-center" />
                                    </button>
                                    :
                                    (
                                      <button
                                        type="submit"

                                        onClick={() => onSubmit(validState)}
                                        className="custom-active-button w-1/2 block mx-auto rounded-md flex items-center justify-center h-12"
                                      >
                                        {(filterLoader || loaderReferenceFromToolComponentInfoLoader) ? (
                                          <Loader customClassName="h-full w-full flex items-center justify-center" />
                                        ) : (
                                          "Next"
                                        )}
                                      </button>
                                    )
                                }
                              </div>
                            </div>

                          </div>
                        </form>
                      </Accordion.Body>
                    </div>
                  </Accordion.Item>

                  {/* Instruction Accordian here */}

                  <Accordion.Item eventKey="2">
                    <Accordion.Header onClick={() => handleAccordionPanel("2")}>
                      INSTRUCTIONS
                    </Accordion.Header>
                    <div className="instruction-floating-box rounded-md">
                      <Accordion.Body>
                        {/* <p>Instructions to the candidates:-</p> */}
                        <ul>
                          {isEditing &&
                            EditQuestionData &&
                            EditQuestionData?.instruction ? (
                            <li>
                              {stripHtmlTags(
                                EditQuestionData?.instruction?.heading
                              ) && (
                                  <>
                                    <strong>
                                      {stripHtmlTags(
                                        EditQuestionData?.instruction?.heading
                                      )}
                                    </strong>
                                    <br />
                                  </>
                                )}
                              {stripPTags(
                                EditQuestionData?.instruction?.content
                              ) && (
                                  <>
                                    {stripHtmlTags(
                                      EditQuestionData?.instruction?.content
                                    )}
                                    <br />
                                  </>
                                )}
                            </li>
                          ) : instructionList?.length > 0 ? (
                            instructionList?.map((instruction, index) => (
                              <li key={instruction?.id} className="">
                                <span className="gap-2">
                                  {index + 1}. &nbsp;
                                  {stripHtmlTags(instruction?.heading)}
                                  <br />
                                  {stripHtmlTags(instruction?.content)}
                                </span>
                                <span
                                  className="mt-1 ml-2 cursor-pointer"
                                  onClick={() =>
                                    handleShowRevisedInstruction(instruction)
                                  }
                                >
                                  <img src={editIcon} alt="edit-icon" />
                                </span>
                              </li>
                            ))
                          ) : (
                            <li>No Instructions</li>
                          )}
                        </ul>

                        <div className="col-12 col-md-12 mt-2">
                          <div className="form-group flex gap-4 m-2">
                            {
                              instructionList?.length === 0 && (
                                <>
                                  <button
                                    type="button"
                                    className="custom-active-button w-1/2 block mx-auto rounded-md"
                                    onClick={handleOpenAddInstruction}
                                  >
                                    Add Instruction
                                  </button>
                                </>
                              )
                            }
                            <button
                              type="submit"
                              onClick={() => handleMultiStepForm("1")}
                              className="custom-active-button w-1/2 block mx-auto rounded-md"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      </Accordion.Body>
                    </div>
                  </Accordion.Item>

                  {/* Questions Accordian here */}
                  <Accordion.Item eventKey="1" className="mb-2 border-0">
                    <Accordion.Header onClick={() => handleAccordionPanel("1")}>
                      QUESTIONS
                    </Accordion.Header>
                    <div className="question-floating-box rounded-md">
                      <Accordion.Body>
                        <Droppable droppableId="question">
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className={snapshot.isDraggingOver ? "droppable-over" : "droppable"}
                            >
                              <div className="flex justify-between items-center text-md font-semibold mb-3">
                                {/* Left side */}
                                <p className="text-[16px] font-extrabold m-0">
                                  Filtered Total Question Based on Class and Subject: {questionCount || 0}
                                </p>
                                <div className="flex gap-1 m-1">
                                  <input
                                    value={qIdSearchInput}
                                    onChange={(e) => {
                                      setQIdSearchInput(e.target.value);
                                      if (!e.target.value.trim()) {
                                        // Clear search when input is cleared
                                        setSearchedQuestions(null);
                                        setSearchMessage("");
                                        setIsApiSearchActive(false);
                                        setAllSearchedQuestions([]);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleQIdSearchWithAPI();
                                      }
                                    }}
                                    type="text"
                                    className="form-control"
                                    placeholder="Type Q_ID (e.g., 1, 2, 3 or Q1, Q2, Q3)"
                                  />
                                  <Button
                                    className="custom-active-button rounded-lg m-2"
                                    onClick={handleQIdSearchWithAPI}
                                    disabled={!qIdSearchInput.trim()}
                                  >
                                    Search
                                  </Button>
                                  {qIdSearchInput && (
                                    <Button
                                      className="custom-inactive-button rounded-lg m-2"
                                      onClick={() => {
                                        setQIdSearchInput("");
                                        setSearchedQuestions(null);
                                        setSearchMessage("");
                                        setIsApiSearchActive(false);
                                        setAllSearchedQuestions([]);
                                      }}
                                    >
                                      Clear
                                    </Button>
                                  )}
                                </div>
                                {/* Right side */}
                                <div className="flex items-center gap-1 text-sm">
                                  <span>Select OR/Main Types</span>
                                  <span
                                    data-tooltip-id="info-tooltip"
                                    data-tooltip-content="Here you can choose between OR type and Main type questions by dragging in question paper."
                                    className="cursor-pointer text-blue-600 hover:text-blue-800"
                                  >
                                    <Info size={18} />
                                  </span>
                                </div>

                                {/* Tooltip */}
                                <Tooltip
                                  id="info-tooltip"
                                  place="top"
                                  style={{
                                    backgroundColor: "#1f2937", // Tailwind gray-800
                                    color: "#fff",
                                    borderRadius: "0.5rem",
                                    padding: "6px 10px",
                                    fontSize: "0.875rem",
                                    zIndex: 9999,
                                  }}
                                />
                              </div>

                              <div style={{ maxHeight: "600px", overflowY: "auto", overflowX: "hidden" }}>
                                {Array.isArray(validState?.questionMarks) && validState.questionMarks.length > 0 && (
                                  <>
                                    <div style={{ marginBottom: "15px", padding: "10px", backgroundColor: "#f3f4f6", borderRadius: "6px" }}>
                                      <strong>Selected Marks: </strong>
                                      <span>{validState.questionMarks.join(", ")}</span>
                                    </div>
                                    {
                                      searchMessage && (
                                        <strong className="justify-center !text-red-500">
                                          {searchMessage}
                                        </strong>
                                      )
                                    }

                                  </>
                                )}
                                <ul style={{ listStyle: "none", padding: 0 }}>
                                  {!!currentPageQuestions?.length ? (
                                    (currentPageQuestions as any[]).map((item: any, index: number) => (
                                      <Draggable key={item?.id?.toString()} draggableId={item?.id?.toString()} index={index}>
                                        {(provided, snapshot) => (
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            className={snapshot.isDragging ? "draggable-dragging" : "draggable"}
                                          >
                                            <li key={item.id} style={{ marginTop: "20px" }}>
                                              <div className="flex justify-between items-center">
                                                <div className="flex items-center">
                                                  <Form.Check
                                                    aria-label="option 1"
                                                    checked={selectedQuestionIds.includes(Number(item.id))}
                                                    onChange={() => handleQuestionCheckboxToggle(item)}
                                                  />
                                                  <span className="text-xl !text-red-600 !text-bold m-1">Q.{index + 1}{" "}</span>
                                                  <div
                                                    className="question-html"
                                                    dangerouslySetInnerHTML={{
                                                      __html: DOMPurify.sanitize(item.question_text),
                                                    }}
                                                  />
                                                  <span
                                                    style={{ marginLeft: "5px", cursor: "pointer" }}
                                                    onClick={() => handleShowRevisedQuestion(item.id)}
                                                  >
                                                    <img src={editIcon} alt="Edit" />
                                                  </span>
                                                </div>
                                                <div className="flex">
                                                  <select
                                                    value={item.displayType || "Main"}
                                                    onChange={(e) =>
                                                      handleQuestionDisplayTypeChange(item.id, e.target.value as any)
                                                    }
                                                    style={{ marginLeft: "8px" }}
                                                  >
                                                    <option value="Main">Main Question</option>
                                                    <option value="OR">OR Question</option>
                                                  </select>
                                                  <strong className="text-xl m-2">[{item?.max_marks}]</strong>
                                                </div>
                                              </div>

                                              {item.question_options && item.question_options.length > 0 && (
                                                <div
                                                  style={{
                                                    marginLeft: "60px",
                                                    marginTop: "4px",
                                                    display: optionsUseGrid(item.question_options) ? "grid" : "flex",
                                                    gridTemplateColumns: optionsUseGrid(item.question_options) ? "1fr 1fr" : undefined,
                                                    flexDirection: optionsUseGrid(item.question_options) ? undefined : "column",
                                                    gap: "4px",
                                                  }}
                                                >
                                                  {item.question_options.map((option, optIndex) => (
                                                    <div
                                                      key={optIndex}
                                                      dangerouslySetInnerHTML={{
                                                        __html: DOMPurify.sanitize(option.data?.data || option.data)
                                                      }}
                                                    />
                                                  ))}
                                                </div>
                                              )}
                                              {Array.isArray(item?.sub_questions) && item.sub_questions.length > 0 && (
                                                <div style={{ marginLeft: "20px", marginTop: "6px" }}>
                                                  {item.sub_questions.map((sq, sIdx) => (
                                                    <div key={sq?.id ?? sIdx} className="d-flex justify-content-between gap-2">
                                                      <div
                                                        dangerouslySetInnerHTML={{
                                                          __html: DOMPurify.sanitize(sq?.question_text || sq?.content || ""),
                                                        }}
                                                      />
                                                      <span>
                                                        <strong>{Number(sq?.max_marks ?? 0)} Marks</strong>
                                                      </span>
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </li>
                                          </div>
                                        )}
                                      </Draggable>
                                    ))
                                  ) : (
                                    <p className="noQuestionFound">No questions found</p>
                                  )}
                                </ul>

                                {/* Pagination Info and Load More Button */}
                                {hasMorePages && accumulatedQuestions.length > 0 && (
                                  <div style={{ padding: "15px", textAlign: "center", borderTop: "1px solid #e5e7eb" }}>
                                    <div style={{ marginBottom: "10px", color: "#6b7280", fontSize: "0.875rem" }}>
                                      Showing {accumulatedQuestions.length} of {totalCount} questions
                                    </div>
                                    <Button
                                      className="custom-active-button rounded-lg"
                                      onClick={loadNextPage}
                                      disabled={isLoadingMore}
                                      style={{ minWidth: "120px" }}
                                    >
                                      {isLoadingMore ? "Loading..." : "Load More"}
                                    </Button>
                                  </div>
                                )}

                                {provided.placeholder}
                              </div>
                            </div>
                          )}
                        </Droppable>

                        {/* Add New Questions button here */}
                        <button
                          className="custom-active-button w-1/2 block mx-auto rounded-md"
                          onClick={handleNewQuestionShow}
                        >
                          Add New Question
                        </button>
                      </Accordion.Body>
                    </div>
                  </Accordion.Item>
                </Accordion>
              </div>
            </div>

            {/* Question paper preview here */}
            <div className="col-12 col-lg-6">
              <div className="right-create-area">
                <div className="flex place-items-center justify-around">
                  <h5 className="text-center text-2xl">
                    {remainingQuestions} Questions Remaining
                  </h5>
                  <button className="custom-active-button rounded-lg w-1/3 m-2" onClick={() => handlePreviewModalShow()}>Preview</button>
                </div>
                <section className="question-paper rounded-md">
                  <div className="container-fluid" ref={contentRef}>
                    <div className="top-area">
                      <div className="left-area">
                        <h6>
                          [Total No. of Questions:{" "}
                          {isEditing
                            ? !!sectionQuestions["section_1"]?.length
                              ? sectionQuestions["section_1"]?.length
                              : EditQuestionData?.total_questions
                            : initialState?.data_json &&
                              initialState?.data_json.length > 0
                              ? initialState?.total_questions
                              : totalQuestionsCount}
                          ]
                        </h6>
                        <input
                          value={qpCode}
                          onChange={(e) => setQpCode(e.target.value)}
                          type="text"
                          className="form-control my-4"
                          placeholder="Type question paper code"
                        />
                      </div>

                      <div className="right-area">
                        <h6>[Total No. of Pages: {pageCount}]</h6>
                      </div>
                    </div>

                    <div className="subject-area-wrapper">
                      <div className="subject-area">
                        <h4>
                          <input
                            value={
                              isEditing ? EditHeaderData?.header_1 : headerOne
                            }
                            onChange={(e) => setHeaderOne(e.target.value)}
                            type="text"
                            className="form-control"
                            placeholder="Type header 1"
                          />
                        </h4>
                        <h2>
                          <input
                            value={
                              isEditing ? EditHeaderData?.header_2 : headerTwo
                            }
                            onChange={(e) => setHeaderTwo(e.target.value)}
                            type="text"
                            className="form-control"
                            placeholder="Type header 2"
                          />
                        </h2>
                        <h2>
                          {isEditing
                            ? EditQuestionData?.grade?.title
                            : classData?.title}
                        </h2>
                        {previewSubjectData?.title && (
                          <h2>
                            {isEditing
                              ? EditQuestionData?.subject?.title
                              : previewSubjectData?.title}
                          </h2>
                        )}
                        <h4>
                          {isEditing
                            ? EditQuestionData?.type_test
                            : initialState?.type_test}
                        </h4>
                      </div>
                      <div className="set-indicator">
                        <h3>
                          <strong>
                            {EditQuestionData?.tag
                              ? EditQuestionData.tag.length > 10
                                ? EditQuestionData.tag.slice(0, 10)
                                : EditQuestionData.tag
                              : ""}
                          </strong>
                        </h3>
                      </div>
                    </div>

                    <div className="exam-details-area">
                      <p>
                        [Time:{" "}
                        {isEditing
                          ? EditQuestionData?.timing
                          : initialState?.timing}{" "}
                        hrs]
                      </p>
                      <p>
                        [Max. Marks:{" "}
                        {isEditing
                          ? EditQuestionData?.question_paper_marks
                          : initialState?.total_marks}
                        ]
                      </p>
                    </div>

                    <div className="instruction-area">
                      {/* <p>Instructions to the candidates:manual</p> */}
                      <ul>
                        {isEditing &&
                          EditQuestionData &&
                          EditQuestionData?.instruction ? (
                          <li>
                            <strong>
                              {EditQuestionData?.instruction?.heading}
                            </strong>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: EditQuestionData?.instruction?.content,
                              }}
                            />
                          </li>
                        ) : instructionList?.length > 0 ? (
                          instructionList?.map((instruction, index) => (
                            <li key={instruction?.id}>
                              <strong>{instruction?.heading}</strong>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: instruction?.content,
                                }}
                              />
                            </li>
                          ))
                        ) : (
                          <li>No Instructions</li>
                        )}
                      </ul>
                    </div>

                    {isEditing
                      ? (() => {
                        // Group sections by section letter for editing mode
                        const groupedBySection: Record<string, any[]> = {};

                        EditQuestionData?.data_json?.forEach((item, sectionIndex) => {
                          const sectionLetter = item?.section;
                          if (sectionLetter && isNaN(sectionLetter)) {
                            if (!groupedBySection[sectionLetter]) {
                              groupedBySection[sectionLetter] = [];
                            }
                            groupedBySection[sectionLetter].push({
                              ...item,
                              originalIndex: sectionIndex,
                            });
                          }
                        });

                        return Object.entries(groupedBySection).map(([sectionLetter, sections]) => {
                          // Calculate total questions across all subsections in this group
                          const totalQuestionsInGroup = sections.reduce((sum, sec) => sum + (sec?.no_of_ques || 0), 0);
                          const currentQuestionsInGroup = sections.reduce((sum, sec) => {
                            return sum + (countNonOr(sectionQuestions[`section_${sec?.section}_${sec.originalIndex}`]) || 0);
                          }, 0);

                          const firstSection = sections[0];
                          const groupKey = `section_${sectionLetter}`;
                          // debugger;
                          return (
                            <div key={groupKey}>
                              {/* Group Header - Single Section Header */}
                              <div className="d-flex justify-between mb-2">
                                <h4></h4>
                                <div className="text-center">
                                  <h4>Section {sectionLetter}</h4>
                                  <small className="text-primary font-semibold">
                                    Subject: {getSubjectNameById(firstSection?.subject_id)}
                                  </small>
                                  {/* {sectionSubjects[`section_${sectionLetter}_${firstSection.originalIndex}`] && (
                                    <small className="text-primary font-semibold">
                                      Subject: {getSubjectNameById(sectionSubjects[`section_${sectionLetter}_${firstSection.originalIndex}`])}
                                      Subject: {getSubjectNameById(firstSection?.subject_id)} test
                                    </small>
                                  )} */}
                                  <br />
                                  <small className="text-muted text-red-600">
                                    {currentQuestionsInGroup} / {totalQuestionsInGroup} questions
                                  </small>
                                </div>
                                <img
                                  className={`collapse-icon cursor-pointer ${collapsedSections[groupKey]
                                    ? "rotate"
                                    : ""
                                    }`}
                                  src={collapseIcon}
                                  alt="collapseIcon"
                                  onClick={() => toggleCollapse(groupKey)}
                                  title={
                                    collapsedSections[groupKey]
                                      ? "Expand Section"
                                      : "Collapse Section"
                                  }
                                />
                              </div>

                              {/* Individual subsections grouped by marks */}
                              {!collapsedSections[groupKey] && sections.map((item) => {
                                const sectionIndex = item.originalIndex;
                                return (
                                  <Droppable
                                    key={`section_${item?.section}_${sectionIndex}`}
                                    droppableId={`section_${item?.section}_${sectionIndex}`}
                                  >
                                    {(provided, snapshot) => {
                                      const sectionKey = `section_${item?.section}_${sectionIndex}`;
                                      const questionsCount = countNonOr(sectionQuestions[sectionKey]) || 0;
                                      const isEmpty = !sectionQuestions[sectionKey] || sectionQuestions[sectionKey].length === 0;

                                      return (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.droppableProps}
                                          className={
                                            snapshot.isDraggingOver
                                              ? "droppable-over"
                                              : "droppable"
                                          }
                                          style={{
                                            marginLeft: '20px',
                                            marginBottom: '10px',
                                            minHeight: snapshot.isDraggingOver ? '100px' : (isEmpty ? '60px' : 'auto'),
                                            padding: snapshot.isDraggingOver ? '15px' : (isEmpty ? '10px' : '0')
                                          }}
                                        >
                                          {/* Subsection indicator showing marks */}
                                          <div className="d-flex justify-content-between align-items-center mb-1">
                                            <div className="text-muted small">
                                              <em>{item?.max_marks} Marks - {questionsCount} / {item?.no_of_ques}</em>
                                            </div>
                                            <img
                                              className={`collapse-icon cursor-pointer ${collapsedSections[`${sectionKey}_marks_${item?.max_marks}`]
                                                ? "rotate"
                                                : ""
                                                }`}
                                              src={collapseIcon}
                                              alt="collapseIcon"
                                              onClick={() => toggleCollapse(`${sectionKey}_marks_${item?.max_marks}`)}
                                              title={
                                                collapsedSections[`${sectionKey}_marks_${item?.max_marks}`]
                                                  ? "Expand Marks Subsection"
                                                  : "Collapse Marks Subsection"
                                              }
                                              style={{ width: '20px', height: '20px', marginLeft: '8px' }}
                                            />
                                          </div>
                                          {/* Section wise Instructions here */}
                                          {!collapsedSections[`${item?.section}_${sectionIndex}`] && !collapsedSections[`${sectionKey}_marks_${item?.max_marks}`] && (
                                            <>
                                              {item?.section &&
                                                isNaN(item?.section) &&
                                                stripPTags(item?.instruction) && (
                                                  <div className="">
                                                    <strong className="grid place-items-center mt-1 mb-2">{stripPTags(item?.instruction)}</strong>
                                                  </div>
                                                )}

                                              <div
                                                className="question-area"
                                                style={{
                                                  minHeight: isEmpty ? '40px' : 'auto',
                                                  padding: isEmpty ? '10px 0' : '0'
                                                }}
                                              >
                                                {sectionQuestions[sectionKey]?.map((question, questionIndex) => (
                                                  <Draggable
                                                    key={question.id.toString()}
                                                    draggableId={question.id.toString()}
                                                    index={questionIndex}
                                                  >
                                                    {(provided, snapshot) => (
                                                      <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className={
                                                          snapshot.isDragging
                                                            ? "draggable-dragging"
                                                            : "draggable"
                                                        }
                                                      >
                                                        <div
                                                          className="d-flex justify-content-between flex-column"
                                                          style={{
                                                            // border: "1px solid #e5e7eb",
                                                            // borderRadius: 8,
                                                            // padding: "12px",
                                                            // marginBottom: "10px",
                                                            // background: "#fff",
                                                          }}
                                                        >
                                                          <div className="d-flex justify-content-between">
                                                            <div className="d-flex flex-column gap-1" key={question.id} style={{ width: "100%" }}>
                                                              {question?.displayType === "OR" && (
                                                                <div className="text-center mb-1">
                                                                  <strong>OR</strong>
                                                                </div>
                                                              )}
                                                              <div className="d-flex gap-2 align-items-start">
                                                                <span
                                                                  style={{
                                                                    fontSize: "1.1rem",
                                                                    minWidth: "28px",
                                                                  }}
                                                                >
                                                                  {(
                                                                    sectionQuestions[`section_${item?.section}_${sectionIndex}`]
                                                                      .slice(0, questionIndex + 1)
                                                                      .filter((q: any) => q?.displayType !== "OR").length
                                                                  )}.
                                                                </span>
                                                                <div style={{ width: "100%" }}>
                                                                  {renderContent(
                                                                    question?.question_text
                                                                  )}
                                                                </div>
                                                              </div>
                                                            </div>
                                                            <div className="ms-3 text-nowrap">
                                                              <span>
                                                                [
                                                                {getQuestionEffectiveMarks(question)}
                                                                ]
                                                              </span>
                                                            </div>
                                                          </div>
                                                          <div
                                                            className="options"
                                                            style={{
                                                              marginTop: "6px",
                                                              paddingLeft: "36px",
                                                              display: optionsUseGrid(question?.question_options) ? "grid" : "flex",
                                                              gridTemplateColumns: optionsUseGrid(question?.question_options) ? "1fr 1fr" : undefined,
                                                              flexDirection: optionsUseGrid(question?.question_options) ? undefined : "column",
                                                              gap: "4px",
                                                            }}
                                                          >
                                                            {question?.question_options?.map(
                                                              (option, optionIndex) => (
                                                                <p
                                                                  key={optionIndex}
                                                                  className="d-flex mb-0"
                                                                >
                                                                  {indexToLetter(
                                                                    optionIndex
                                                                  )}
                                                                  .
                                                                  {renderContent(
                                                                    option?.data
                                                                      ?.data ||
                                                                    option?.data
                                                                  )}
                                                                </p>
                                                              )
                                                            )}
                                                            {Array.isArray(question?.sub_questions) && question.sub_questions.length > 0 && (
                                                              <div className="mt-1 d-flex flex-column" style={{ gap: "6px" }}>
                                                                {question.sub_questions.map((sq, sIdx) => (
                                                                  <div key={sq?.id ?? sIdx} className="d-flex justify-content-between gap-2 align-items-start">
                                                                    <div className="d-flex gap-2">
                                                                      <strong>{indexToLetter(sIdx)}.</strong>
                                                                      <div>{renderContent(sq?.question_text || sq?.content || "")}</div>
                                                                    </div>
                                                                    <span><strong>{Number(sq?.max_marks ?? 0)} </strong></span>
                                                                  </div>
                                                                ))}
                                                              </div>
                                                            )}
                                                          </div>
                                                        </div>
                                                      </div>
                                                    )}
                                                  </Draggable>
                                                ))}
                                                {provided.placeholder}
                                                {isEmpty && !snapshot.isDraggingOver && (
                                                  <div style={{
                                                    color: '#999',
                                                    fontStyle: 'italic',
                                                    padding: '10px',
                                                    textAlign: 'center',
                                                    minHeight: '30px'
                                                  }}>
                                                    Drop questions here
                                                  </div>
                                                )}
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      );
                                    }}
                                  </Droppable>
                                );
                              })}
                            </div>
                          );
                        });
                      })()
                      : !isEditing &&
                      Object.entries(groupedSections).map(([groupKey, sections]: [string, any[]]) => {
                        const firstSection = sections[0];
                        const subjectId = firstSection?.subject_id;
                        const sectionLetter = firstSection?.section;

                        // Calculate total questions across all subsections in this group
                        const totalQuestionsInGroup = sections.reduce((sum, sec) => sum + (sec?.no_of_ques || 0), 0);
                        const currentQuestionsInGroup = sections.reduce((sum, sec) => {
                          return sum + (countNonOr(sectionQuestions[sec.sectionKey]) || 0);
                        }, 0);

                        return (
                          <div key={groupKey}>
                            {/* Group Header */}
                            {sectionLetter && isNaN(sectionLetter) && (
                              <div className="d-flex justify-between mb-2">
                                <h4></h4>
                                <div className="text-center">
                                  <h4>Section {sectionLetter}</h4>
                                  {subjectId && (
                                    <small className="text-primary font-semibold">
                                      Subject: {getSubjectNameById(subjectId)}
                                    </small>
                                  )}
                                  <br />
                                  <small className="text-muted text-red-600">
                                    {currentQuestionsInGroup} / {totalQuestionsInGroup} questions
                                  </small>
                                </div>
                                <img
                                  className={`collapse-icon cursor-pointer ${collapsedSections[groupKey]
                                    ? "rotate"
                                    : ""
                                    }`}
                                  src={collapseIcon}
                                  alt="collapseIcon"
                                  onClick={() => toggleCollapse(groupKey)}
                                  title={
                                    collapsedSections[groupKey]
                                      ? "Expand Section"
                                      : "Collapse Section"
                                  }
                                />
                              </div>
                            )}

                            {/* Individual subsections for different mark requirements */}
                            {!collapsedSections[groupKey] && sections.map((item, subIndex) => {
                              const sectionIndex = item.originalIndex;
                              return (
                                <Droppable
                                  key={`section_${item?.section}_${sectionIndex}`}
                                  droppableId={`section_${item?.section}_${sectionIndex}`}
                                >
                                  {(provided, snapshot) => {
                                    const sectionKey = `section_${item?.section}_${sectionIndex}`;
                                    const questionsCount = countNonOr(sectionQuestions[sectionKey]) || 0;
                                    const isEmpty = !sectionQuestions[sectionKey] || sectionQuestions[sectionKey].length === 0;

                                    return (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className={
                                          snapshot.isDraggingOver
                                            ? "droppable-over"
                                            : "droppable"
                                        }
                                        style={{
                                          marginLeft: '20px',
                                          marginBottom: '10px',
                                          minHeight: snapshot.isDraggingOver ? '100px' : (isEmpty ? '60px' : 'auto'),
                                          padding: snapshot.isDraggingOver ? '15px' : (isEmpty ? '10px' : '0')
                                        }}
                                      >
                                        {/* Subsection indicator showing marks */}
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                          <div className="text-muted small">
                                            <em>{item?.max_marks} Marks - {questionsCount} / {item?.no_of_ques} testquestions</em>
                                          </div>
                                          <img
                                            className={`collapse-icon cursor-pointer ${collapsedSections[`${sectionKey}_marks_${item?.max_marks}`]
                                              ? "rotate"
                                              : ""
                                              }`}
                                            src={collapseIcon}
                                            alt="collapseIcon"
                                            onClick={() => toggleCollapse(`${sectionKey}_marks_${item?.max_marks}`)}
                                            title={
                                              collapsedSections[`${sectionKey}_marks_${item?.max_marks}`]
                                                ? "Expand Marks Subsection"
                                                : "Collapse Marks Subsection"
                                            }
                                            style={{ width: '20px', height: '20px', marginLeft: '8px' }}
                                          />
                                        </div>
                                        {/* Implementing the Multi subject Feature in the  */}
                                        {/* Section wise Instructions here */}
                                        {!collapsedSections[`${item?.section}_${sectionIndex}`] && !collapsedSections[`${sectionKey}_marks_${item?.max_marks}`] && (
                                          <>
                                            {item?.section &&
                                              isNaN(item?.section) &&
                                              stripPTags(item?.instruction) && (
                                                <>
                                                  {/* <h5>Instructions to the section:-</h5> */}
                                                  <p className="">
                                                    {stripPTags(item?.instruction)}
                                                  </p>
                                                </>
                                              )}

                                            <div
                                              className="question-area"
                                              style={{
                                                minHeight: isEmpty ? '40px' : 'auto',
                                                padding: isEmpty ? '10px 0' : '0'
                                              }}
                                            >
                                              {sectionQuestions[sectionKey]?.map((question, questionIndex) => (
                                                <Draggable
                                                  key={question.id.toString()}
                                                  draggableId={question.id.toString()}
                                                  index={questionIndex}
                                                >
                                                  {(provided, snapshot) => (
                                                    <div
                                                      ref={provided.innerRef}
                                                      {...provided.draggableProps}
                                                      {...provided.dragHandleProps}
                                                      className={
                                                        snapshot.isDragging
                                                          ? "draggable-dragging"
                                                          : "draggable"
                                                      }
                                                    >
                                                      {question?.displayType !== "OR" && (
                                                        <div
                                                          style={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                          }}
                                                        >
                                                          <p key={question.id} className="d-flex gap-2">
                                                            <span style={{ fontSize: "18px", fontWeight: "bold" }}>
                                                              {
                                                                sectionQuestions[`section_${item?.section}_${sectionIndex}`]
                                                                  .slice(0, questionIndex + 1)
                                                                  .filter((q: any) => q?.displayType !== "OR").length
                                                              }.
                                                            </span>
                                                            {renderContent(question?.question_text)}
                                                          </p>
                                                          <p>
                                                            <span>[Marks: {getQuestionEffectiveMarks(question)}]</span>
                                                          </p>
                                                        </div>
                                                      )}

                                                      {question?.displayType === "OR" && (
                                                        <div style={{ display: "flex", flexDirection: "column" }}>
                                                          {/* Line 1: Number + OR */}
                                                          <div className="text-center">
                                                            <span style={{ fontSize: "1.1rem" }}>
                                                              {
                                                                sectionQuestions[`section_${item?.section}`]
                                                                  ?.slice(0, questionIndex + 1)
                                                                  ?.filter((q: any) => q?.displayType !== "OR").length
                                                              }
                                                              . <strong>OR</strong>
                                                            </span>
                                                          </div>

                                                          {/* Line 2: Question + Marks */}
                                                          <div
                                                            style={{
                                                              display: "flex",
                                                              justifyContent: "space-between",
                                                              alignItems: "flex-start",
                                                              marginTop: "4px",
                                                            }}
                                                          >
                                                            <div>{renderContent(question?.question_text)}</div>
                                                            <span>[Marks: {getQuestionEffectiveMarks(question)}]</span>
                                                          </div>
                                                        </div>
                                                      )}

                                                      <div
                                                        className="options"
                                                        style={{
                                                          paddingLeft: "36px",
                                                          display: optionsUseGrid(question?.question_options) ? "grid" : "flex",
                                                          gridTemplateColumns: optionsUseGrid(question?.question_options) ? "1fr 1fr" : undefined,
                                                          flexDirection: optionsUseGrid(question?.question_options) ? undefined : "column",
                                                          gap: "4px",
                                                        }}
                                                      >
                                                        {question?.question_options?.map(
                                                          (option, optionIndex) => (
                                                            <p
                                                              key={optionIndex}
                                                              className="d-flex mb-0"
                                                            >
                                                              {indexToLetter(
                                                                optionIndex
                                                              )}
                                                              . {renderContent(
                                                                option?.data?.data ||
                                                                option?.data
                                                              )}
                                                            </p>
                                                          )
                                                        )}
                                                        {Array.isArray(question?.sub_questions) && question.sub_questions.length > 0 && (
                                                          <div className="mt-1 pl-10">
                                                            {question.sub_questions.map((sq, sIdx) => (
                                                              <div key={sq?.id ?? sIdx} className="d-flex justify-content-between gap-2">
                                                                <div>{renderContent(sq?.question_text || sq?.content || "")}</div>
                                                                <span><strong>{Number(sq?.max_marks ?? 0)} Marks</strong></span>
                                                              </div>
                                                            ))}
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  )}
                                                </Draggable>
                                              ))}
                                              {provided.placeholder}
                                              {isEmpty && !snapshot.isDraggingOver && (
                                                <div style={{
                                                  color: '#999',
                                                  fontStyle: 'italic',
                                                  padding: '10px',
                                                  textAlign: 'center',
                                                  minHeight: '30px'
                                                }}>
                                                  Drop questions here
                                                </div>
                                              )}
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    );
                                  }}
                                </Droppable>
                              );
                            })}
                          </div>
                        );
                      })}
                    {!initialState?.data_json?.length && (
                      <Droppable droppableId="default-section">
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={
                              snapshot.isDraggingOver
                                ? "droppable-over"
                                : "droppable"
                            }
                          >
                            <div className="question-area d-flex">
                              {sectionQuestions["default-section"]?.map(
                                (question, index) => (
                                  <Draggable
                                    key={question.id.toString()}
                                    draggableId={question.id.toString()}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className={
                                          snapshot.isDragging
                                            ? "draggable-dragging"
                                            : "draggable"
                                        }
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                          }}
                                        >
                                          <p
                                            key={question.id}
                                            className="d-flex gap-2"
                                          >
                                            <span
                                              style={{ fontSize: "1.1rem" }}
                                            >
                                              {(
                                                sectionQuestions["default-section"]
                                                  .slice(0, index + 1)
                                                  .filter((q: any) => q?.displayType !== "OR").length
                                              )}. {question?.displayType === "OR" && (
                                                <strong style={{ marginRight: 6 }}>OR</strong>
                                              )}
                                            </span>
                                            {renderContent(
                                              question?.question_text
                                            )}
                                          </p>
                                          <p>
                                            <span>
                                              [Marks: {getQuestionEffectiveMarks(question)}]</span>
                                          </p>
                                        </div>
                                        <div
                                          className="options"
                                          style={{
                                            paddingLeft: "36px",
                                            display: optionsUseGrid(question?.question_options) ? "grid" : "flex",
                                            gridTemplateColumns: optionsUseGrid(question?.question_options) ? "1fr 1fr" : undefined,
                                            flexDirection: optionsUseGrid(question?.question_options) ? undefined : "column",
                                            gap: "4px",
                                          }}
                                        >
                                          {question?.question_options?.map(
                                            (option, optionIndex) => (
                                              <p
                                                key={optionIndex}
                                                className="d-flex mb-0"
                                              >
                                                {indexToLetter(optionIndex)}. {renderContent(
                                                  option?.data?.data ||
                                                  option?.data
                                                )}
                                              </p>
                                            )
                                          )}
                                          {Array.isArray(question?.sub_questions) && question.sub_questions.length > 0 && (
                                            <div className="mt-1">
                                              {question.sub_questions.map((sq, sIdx) => (
                                                <div key={sq?.id ?? sIdx} className="d-flex justify-content-between gap-2">
                                                  <div>{renderContent(sq?.question_text || sq?.content || "")}</div>
                                                  <span><strong>{Number(sq?.max_marks ?? 0)} Marks</strong></span>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                )
                              )}
                              {provided.placeholder}
                            </div>
                          </div>
                        )}
                      </Droppable>
                    )}
                  </div>
                </section>

                {/* submit and reset button */}
                <div className="d-flex gap-2 mb-3">
                  {/* {isEditing ? (
                    <button
                      type="button"
                      className="custom-inactive-button w-1/2 block mx-auto rounded-md mt-4"
                      onClick={handleBack}
                    >
                      Cancel
                    </button>
                  ) : null} */}
                  <button
                    type="button"
                    className="custom-active-button w-1/2 block mx-auto rounded-md mt-4"
                    onClick={handleFormDraftSubmit}
                  >
                    Draft
                  </button>
                  <button
                    type="button"
                    className="custom-active-button w-1/2 block mx-auto rounded-md mt-4"
                    onClick={() => setShowFinalSubmitConfirm(true)}
                  >
                    {EditQuestionData && "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </DragDropContext >
      </div >

      {/* Final submit confirmation modal */}
      <Modal
        show={showFinalSubmitConfirm}
        onHide={() => setShowFinalSubmitConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">Confirm Final Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Are you sure you want to submit this question paper?
            <br />
            <strong>Once submitted, it cannot be edited.</strong>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="custom-inactive-button rounded-md"
            onClick={() => setShowFinalSubmitConfirm(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="custom-active-button rounded-md"
            onClick={() => {
              setShowFinalSubmitConfirm(false);
              handleFormSubmit();
            }}
          >
            Yes, Submit
          </button>
        </Modal.Footer>
      </Modal>

      {/* Edit Question Modal here*/}

      <Modal show={showRevisedQuestion} onHide={handleCloseQuestion} size="lg" >
        <Modal.Header closeButton>
          <Modal.Title className="h5">Edit Question</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleUpdateQuestion}>
            <div className="form-group mb-3">
              <label htmlFor="">
                Question Text<span className="form_required">*</span>
              </label>
              <CKEditor
                initData={editorQuestionsHtml}
                onChange={(event) =>
                  setEditorQuestionsHtml(event.editor?.getData())
                }
              />
            </div>

            {optionEditors?.map((option, index) => (
              <div key={index} className="form-group mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label>Option {index + 1}</label>
                  <button
                    type="button"
                    className="custom-inactive-button rounded-lg"
                    onClick={() => handleRemoveEditor(index)}
                  >
                    Close
                  </button>
                </div>
                <div>
                  <input
                    type="checkbox"
                    className="theme-checkbox"
                    id={`option-${index}`}
                    checked={selectedAnswers.includes(index + 1)}
                    onChange={() => handleCheckboxChange(index)}
                  />
                  <CKEditor
                    initData={option?.data || option?.data?.data}
                    onChange={(event) =>
                      handleOptionEditorChange(index, event.editor.getData())
                    }
                  />
                </div>
              </div>
            ))}

            {[
              "Match Making",
              "Very Short Answer",
              "Short answer",
              "Long",
            ].includes(validState?.questionType) && (
                <div className="form-group mb-4">
                  <label>Answer Text</label>
                  <CKEditor
                    initData={editorAnswerHtml}
                    onChange={(event) =>
                      setEditorAnswerHtml(event.editor.getData())
                    }
                  />
                </div>
              )}

            <div className="form-group">
              <button className="custom-active-button rounded-lg" type="submit">
                Update
              </button>
            </div>
          </form>
        </Modal.Body>
      </ Modal>

      {/* Edit Instruction Modal here */}
      < Modal
        show={showRevisedInstruction}
        onHide={handleCloseInstruction}
        backdrop="static"
        keyboard={false}
        centered
        size="lg"
      >
        <Modal.Header closeButton>Update Instruction</Modal.Header>
        <Modal.Body>
          <form className="horizontal-form" onSubmit={handleUpdateInstruction}>
            <div className="form-group mb-3">
              <textarea
                placeholder="Please Type Your Revised Instruction Heading Here"
                className="form-control mb-3"
                name=""
                id=""
                cols={30}
                rows={5}
                value={stripHtmlTags(selectedInstructionText)}
                onChange={(e) => setSelectedInstructionText(e.target.value)}
              ></textarea>
              <textarea
                placeholder="Please Type Your Revised Instruction Content Here"
                className="form-control"
                name=""
                id=""
                cols={30}
                rows={10}
                value={stripHtmlTags(selectedInstructionContent)}
                onChange={(e) => setSelectedInstructionContent(e.target.value)}
              ></textarea>
            </div>
            <div className="form-group">
              <button className="custom-active-button rounded-lg" type="submit">
                Update
              </button>
            </div>
          </form>
        </Modal.Body>
      </ Modal>

      {/* Add Instruction Modal here */}
      < Modal
        show={showAddInstruction}
        onHide={handleCloseAddInstruction}
        backdrop="static"
        keyboard={false}
        centered
        size="lg"
      >
        <Modal.Header closeButton>Add Instruction</Modal.Header>
        <Modal.Body>
          <form className="horizontal-form" onSubmit={handleAddInstruction}>
            <div className="form-group mb-3">
              <label htmlFor="instructionHeading" className="mb-2">
                Instruction Heading<span className="form_required">*</span>
              </label>
              <textarea
                id="instructionHeading"
                placeholder="Please Type Your Instruction Heading Here"
                className="form-control mb-3"
                name="instructionHeading"
                cols={30}
                rows={5}
                value={selectedInstructionText}
                onChange={(e) => setSelectedInstructionText(e.target.value)}
                required
              ></textarea>
              <label htmlFor="instructionContent" className="mb-2">
                Instruction Content<span className="form_required">*</span>
              </label>
              <textarea
                id="instructionContent"
                placeholder="Please Type Your Instruction Content Here"
                className="form-control"
                name="instructionContent"
                cols={30}
                rows={10}
                value={selectedInstructionContent}
                onChange={(e) => setSelectedInstructionContent(e.target.value)}
                required
              ></textarea>
            </div>
            <div className="form-group">
              <button className="custom-active-button rounded-lg" type="submit">
                Add Instruction
              </button>
            </div>
          </form>
        </Modal.Body>
      </ Modal>

      {/* Grid view  Modal here*/}
      < Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">Question Paper Data</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {gridPreviewData ? (
            <div className="responsive-table">
              <Table responsive bordered className="blueprint-table">
                <thead>
                  <tr>
                    {gridPreviewData?.headers?.map((header, idx) => (
                      <th key={idx} className="table-cell">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {gridPreviewData?.data?.map((item, idx) => (
                    <tr key={idx}>
                      {gridPreviewData?.headers?.map((header, index) => (
                        <td key={index} className="table-cell">
                          {renderCell(item, header)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="form-group">
                <button
                  className="w-full custom-active-button rounded-lg"
                  onClick={handleFinalNext}
                >
                  Next
                </button>
              </div>
            </div>
          ) : (
            <p>Loading...</p>
          )}
        </Modal.Body>
      </ Modal>

      {/* Add Question Modal here  */}
      {/* < Modal
        show={showNewQuestionPaper}
        onHide={handleNewQuestionClose}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">Add New Question</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleAddQuestion}>
            <div className="row">
              <div className="form-group mb-4">
                <label htmlFor="">
                  Question Text<span className="form_required">*</span>
                </label>
                <CKEditor
                  data={editorQuestionHtml}
                  onChange={handleQuestionEditor}
                />
              </div>

              {optionEditors?.map((option, index) => (
                <div key={index} className="col-md-12 mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5>Option {index + 1}</h5>
                    <button
                      type="button"
                      className="custom-inactive-button rounded-lg"
                      onClick={() => handleRemoveEditor(index)}
                    >
                      Close
                    </button>
                  </div>
                  <div>
                    <input
                      type="checkbox"
                      className="theme-checkbox"
                      id={`option-${index}`}
                      checked={selectedAnswers.includes(index + 1)}
                      onChange={() => handleCheckboxChange(index)}
                    />
                    <CKEditor
                      data={option.data || ""}
                      onChange={(event) =>
                        handleOptionEditorChange(index, event.editor.getData())
                      }
                    />
                  </div>
                </div>
              ))}

              {["Multiple-choice", "True / False"].includes(
                validState?.questionType
              ) && (
                  <div className="col-md-4 mb-4">
                    <button
                      className="custom-active-button rounded-lg"
                      type="button"
                      onClick={handleOpenEditor}
                    >
                      Add Option
                    </button>
                  </div>
                )}

              {[
                "Match Making",
                "Very Short Answer",
                "Short answer",
                "Long",
              ].includes(validState?.questionType) && (
                  <div className="form-group mb-4">
                    <label>Answer Text</label>
                    <CKEditor
                      data={editorAnswerHtml}
                      onChange={handleAnswerEditor}
                    />
                  </div>
                )}
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="custom-inactive-button rounded-lg"
            onClick={handleNewQuestionClose}
          >
            Cancel
          </button>
          <button
            className="custom-active-button rounded-lg"
            type="submit"
            onClick={handleAddQuestion}
          >
            Save
          </button>
        </Modal.Footer>
      </ Modal> */}

      {/* Add other questions */}

      <Modal
        show={showNewQuestionPaper}
        onHide={handleNewQuestionClose}
        size="xl"
        fullscreen="lg-down"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">Add New Question</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          <CreateQuestions
            onSuccess={handleNewQuestionSuccess}
            onCancel={handleNewQuestionClose}
            isModal={true}
          />
        </Modal.Body>
      </Modal>


      {/* adding preview modal */}
      <Modal
        show={previewShowModal}
        onHide={handlePreviewModalClose}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter center">
            Question Paper Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {previewPayload ? (
            <LiveViewQuestionPaperComponent
              questionPaperDataOverride={previewPayload}
              isEmbedded
            // setFinalPageCount={setPreviewPageCount}
            />
          ) : (
            <Loader />
          )}
        </Modal.Body>
        <Modal.Footer>
          <button onClick={handlePreviewModalClose}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

    </>
  );
};

export default CreateRoutingBasedManualQuestionPapersComponent;

// Create Manual Question Papers Component done //


// This compoenent is for the Tooltips Components feature

export const MarksToolTipsComponents = ({ gradeId, subjectId, setLoaderReferenceFromToolComponentInfoLoader }: { gradeId?: number; subjectId?: number; loaderReferenceFromToolComponentInfoLoader: boolean; setLoaderReferenceFromToolComponentInfoLoader: (value: boolean) => void; }) => {
  const dispatch = useDispatch<typeof store.dispatch>();
  const subjectList = useTypedSelector(DisplaySubjectDataList);

  const [isLoading, setIsLoading] = useState(false);
  const [tooltipHtml, setTooltipHtml] = useState("<div style='padding:6px 8px'>Select class and subject to view availability</div>");


  useEffect(() => {
    const run = async () => {
      if (!gradeId || !subjectId) {
        setTooltipHtml("<div style='padding:6px 8px'>Select class and subject to view availability</div>");
        return;
      }
      setIsLoading(true);
      setLoaderReferenceFromToolComponentInfoLoader(true);
      try {
        // Fetch for primary subject
        const mainAction: any = await dispatch(
          QuestionListAsync({ gradeId: [Number(gradeId)], subjectId: [Number(subjectId)], isAllDataFetch: false })
        );
        const mainQuestions: any[] = mainAction?.payload?.questions || [];
        const mainCounts = mainQuestions.reduce((acc: Record<string, number>, q: any) => {
          const m = String(Number(q?.max_marks) || 0);
          if (m !== "0") acc[m] = (acc[m] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        // Determine child subjects of selected subject (e.g., PCB)
        const children = Array.isArray(subjectList)
          ? subjectList.filter((s: any) => s?.parent_id === subjectId)
          : [];

        // Fetch per-child counts
        const perChildCounts: Record<number, Record<string, number>> = {};
        await Promise.all(
          children.map(async (child: any) => {
            const act: any = await dispatch(
              QuestionListAsync({ gradeId: [Number(gradeId)], subjectId: [Number(child?.id)], isAllDataFetch: false })
            );
            const qs: any[] = act?.payload?.questions || [];
            const counts = qs.reduce((acc: Record<string, number>, q: any) => {
              const m = String(Number(q?.max_marks) || 0);
              if (m !== "0") acc[m] = (acc[m] || 0) + 1;
              return acc;
            }, {} as Record<string, number>);
            perChildCounts[child.id] = counts;
          })
        );

        // Build marks set across all
        const marksSet = new Set<string>([...Object.keys(mainCounts)]);
        children.forEach((c: any) => {
          Object.keys(perChildCounts[c.id] || {}).forEach((m) => marksSet.add(m));
        });
        const marks = Array.from(marksSet).map(Number).filter((n) => !isNaN(n)).sort((a, b) => a - b);

        // Build HTML table
        const hasChildren = children.length > 0;
        const theadCols = [
          "<th style='padding:6px 8px; text-align:left'>Marks</th>",
          "<th style='padding:6px 8px; text-align:right'>Total</th>",
          ...children.map((c: any) => `<th style='padding:6px 8px; text-align:right'>${(c?.title || "").replace(/</g, "&lt;")}</th>`),
        ].join("");

        const rows = marks.map((m) => {
          const key = String(m);
          const total = mainCounts[key] || 0;
          const perChildTds = children
            .map((c: any) => `<td style='padding:6px 8px; text-align:right'>${perChildCounts[c.id]?.[key] || 0}</td>`)
            .join("");
          return `<tr><td style='padding:6px 8px'>${m}</td><td style='padding:6px 8px; text-align:right'>${total}</td>${perChildTds}</tr>`;
        }).join("");

        const caption = `<div style='font-size:12px; margin-bottom:6px;'>Question availability by marks${hasChildren ? " (with sub-subjects)" : ""}</div>`;
        const tableHtml = `
          ${caption}
          <table style='border-collapse:collapse; width:100%; border:1px solid #374151;'>
            <thead style='background:#111827; color:#fff;'>
              <tr>${theadCols}</tr>
            </thead>
            <tbody>
              ${rows || "<tr><td colspan='" + (2 + children.length) + "' style='padding:6px 8px; text-align:center'>No data</td></tr>"}
            </tbody>
          </table>`;

        setTooltipHtml(tableHtml);
      } catch (e) {
        setTooltipHtml("<div style='padding:6px 8px'>Failed to load availability</div>");
      } finally {
        setIsLoading(false);
        setLoaderReferenceFromToolComponentInfoLoader(false);
      }
    };
    run();
  }, [gradeId, subjectId, dispatch, subjectList]);

  const infoLabel = isLoading ? "Loading..." : "Info";

  return (
    <>
      <div className="col-12 col-md-6 mb-2">
        <div className="form-group">
          <label className="flex items-center gap-1">
            {infoLabel}
            <span
              data-tooltip-id="availability-tooltip"
              data-tooltip-html={tooltipHtml}
              className="cursor-pointer text-blue-600 hover:text-blue-800"
            >
              <Info size={18} />
            </span>
          </label>

          <Tooltip
            id="availability-tooltip"
            place="right"
            html
            style={{
              backgroundColor: "#1f2937",
              color: "#fff",
              borderRadius: "0.5rem",
              padding: "6px 10px",
              fontSize: "0.875rem",
              zIndex: 9999,
              maxWidth: 420,
            }}
          />
        </div>
      </div>
    </>
  );
}